// ✅ diamond-api.js loaded
console.log("✅ diamond-api.js loaded");

// Card media (data / capability detection / rendering / interaction for
// `.diamond-card__media`) lives in diamond-card-media.js, loaded before
// this file, and is used via the global `DiamondCardMedia` namespace —
// see renderLiveDiamondResults() below and that file's header comment.
//
// Fail fast and clearly if the load order in sections/diamond-search.liquid
// is ever broken (script missing, 404, blocked, syntax error, etc.) — a
// controlled error naming the missing dependency here beats an obscure
// "DiamondCardMedia is not defined" thrown later inside a click/filter
// handler deep in renderLiveDiamondResults(). This is a guard, not a
// second implementation of media rendering.
if (typeof DiamondCardMedia === "undefined") {
  throw new Error(
    "diamond-api.js: DiamondCardMedia is not defined. diamond-card-media.js " +
      "failed to load or is missing from sections/diamond-search.liquid — it " +
      "must be included with a <script> tag before diamond-api.js."
  );
}

// Guest Saved Diamonds lives in diamond-saved.js (window.DiamondSaved),
// loaded once from layout/theme.liquid before this file. Persistence is
// owned there — this file only restores/toggles the existing heart.
// Search still works if the store is unavailable; hearts then degrade
// to unsaved. Same-tab Saved mutations arrive via
// ivanov:saved-diamonds-change; cross-tab via the native storage event.

if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
  if (!window.__ivanovSavedDiamondsHeartSyncBound) {
    window.__ivanovSavedDiamondsHeartSyncBound = true;
    window.addEventListener("ivanov:saved-diamonds-change", function () {
      if (window.DiamondSaved && typeof window.DiamondSaved.syncRenderedHearts === "function") {
        window.DiamondSaved.syncRenderedHearts();
      }
    });
  }
}

// ————————————————————————————————————————————
// 1) Load diamonds from the IvanovAPI backend once into memory
// ————————————————————————————————————————————
let allDiamonds = [];

// Monotonic request id so a slower/older response cannot overwrite
// newer results or appear to restore a previous origin selection.
let reloadRequestId = 0;

// Abort the previous FULL/REPLACE search when a newer replace begins.
// Load More (append) is intentionally not cancelled by this controller.
let reloadAbortController = null;

function isIntentionalAbortError(err) {
  if (!err) return false;
  if (err.name === "AbortError") return true;
  const cause = err.cause;
  return !!(cause && cause.name === "AbortError");
}

// Stage 2B pagination. Replace searches use reloadRequestId + offset 0.
// Load More is the only append path; appendRequestId is invalidated by
// every replace so a stale chunk cannot land on a newer query.
const DIAMOND_PAGE_SIZE = 36;
let appendRequestId = 0;
let appendInFlight = false;
// True Nivoda inventory total from diamonds_by_query.result_count.
// Not the rendered card count and not total_count (page/window size).
let resultCount = null;
let hasMore = false;
// Next server window. Advances by the requested/returned window, not
// by the deduplicated visible-card count.
let nextOffset = 0;

// One retry listener on #diamondGrid. Bound once so repeated error
// states cannot accumulate click handlers.
let diamondResultsRetryBound = false;

// Neutral placeholders while inventory is in flight. Not cards: no
// fake prices, titles, or diamond data. Count matches a short Grid
// page so List rows stay compact too.
const DIAMOND_SKELETON_COUNT = 6;

const ORIGIN_PARAM = "origin";
const ORIGIN_NATURAL = "natural";
const ORIGIN_LAB_GROWN = "lab-grown";

const SHAPE_PARAM = "shape";
// Canonical shopper slug → shopper-facing label. Source of truth for
// URL restore, selected Shape, Hero H1, and the storefront `shape` request
// value. Backend owns Nivoda wire mapping; do not add HEXAGONAL / TRILLIANT
// / OLD MINER (etc.) here. Heptagon is backend-only in V1 — not listed.
const SHAPE_SLUG_TO_LABEL = {
  round: "Round",
  oval: "Oval",
  cushion: "Cushion",
  princess: "Princess",
  emerald: "Emerald",
  radiant: "Radiant",
  pear: "Pear",
  heart: "Heart",
  asscher: "Asscher",
  marquise: "Marquise",
  hexagon: "Hexagon",
  triangle: "Triangle",
  trillion: "Trillion",
  "square-emerald": "Square Emerald",
  "square-radiant": "Square Radiant",
  square: "Square",
  "old-mine": "Old Mine Cut",
  rose: "Rose Cut",
  "old-european": "Old European Cut",
  octagon: "Octagon",
  pentagon: "Pentagon",
  shield: "Shield",
  lozenge: "Lozenge",
  kite: "Kite",
  calf: "Calf",
  briolette: "Briolette",
  trapezoid: "Trapezoid",
  baguette: "Baguette",
  "tapered-baguette": "Tapered Baguette",
  "half-moon": "Half Moon",
  bullet: "Bullet",
};
const PRIMARY_SHAPE_SLUGS = [
  "round",
  "oval",
  "cushion",
  "princess",
  "emerald",
  "radiant",
  "pear",
  "heart",
  "asscher",
  "marquise",
];
const SHAPE_LABEL_TO_SLUG = {};
const MORE_SHAPE_LABELS = new Set();
Object.keys(SHAPE_SLUG_TO_LABEL).forEach((slug) => {
  const label = SHAPE_SLUG_TO_LABEL[slug];
  SHAPE_LABEL_TO_SLUG[label] = slug;
  SHAPE_LABEL_TO_SLUG[label.toLowerCase()] = slug;
  if (!PRIMARY_SHAPE_SLUGS.includes(slug)) {
    MORE_SHAPE_LABELS.add(label);
  }
});
window.IvanovShapeMap = SHAPE_SLUG_TO_LABEL;

// Sort is search state (querystring), not presentation state.
// Featured is the canonical default: omit `sort` from the URL and API.
const SORT_PARAM = "sort";
const SORT_VALUES = [
  "featured",
  "price-asc",
  "price-desc",
  "carat-desc",
  "carat-asc",
];
const SORT_DEFAULT = "featured";
const SORT_ELEMENT_ID = "diamondSort";

const COLOR_PARAM = "color";
const COLOR_MIN_PARAM = "colorMin";
const COLOR_MAX_PARAM = "colorMax";
// URL / UI / API color grades (D–K dual-range).
const COLOR_VALUES = ["D", "E", "F", "G", "H", "I", "J", "K"];
const COLOR_DEFAULT_MIN = "D";
const COLOR_DEFAULT_MAX = "K";

const CLARITY_PARAM = "clarity";
const CLARITY_MIN_PARAM = "clarityMin";
const CLARITY_MAX_PARAM = "clarityMax";
// URL / UI / API clarity grades (FL–SI2 dual-range).
const CLARITY_VALUES = ["FL", "IF", "VVS1", "VVS2", "VS1", "VS2", "SI1", "SI2"];
const CLARITY_DEFAULT_MIN = "FL";
const CLARITY_DEFAULT_MAX = "SI2";

const CUT_PARAM = "cut";
const CUT_MIN_PARAM = "cutMin";
const CUT_MAX_PARAM = "cutMax";
// URL / UI cut grades (Fair–Ideal dual-range). API uses short codes via map below.
const CUT_VALUES = ["Fair", "Good", "Very Good", "Excellent", "Ideal"];
const CUT_DEFAULT_MIN = "Fair";
const CUT_DEFAULT_MAX = "Ideal";
const CUT_LABEL_TO_API_VALUE = {
  Ideal: "ID",
  Excellent: "EX",
  "Very Good": "VG",
  Good: "GD",
  Fair: "FR",
};

const POLISH_PARAM = "polish";
const POLISH_MIN_PARAM = "polishMin";
const POLISH_MAX_PARAM = "polishMax";
// URL / UI polish grades (Good–Excellent dual-range). API uses short codes via map below.
const POLISH_VALUES = ["Good", "Very Good", "Excellent"];
const POLISH_DEFAULT_MIN = "Good";
const POLISH_DEFAULT_MAX = "Excellent";
const POLISH_LABEL_TO_API_VALUE = {
  Excellent: "EX",
  "Very Good": "VG",
  Good: "GD",
};

const SYMMETRY_PARAM = "symmetry";
const SYMMETRY_MIN_PARAM = "symmetryMin";
const SYMMETRY_MAX_PARAM = "symmetryMax";
// URL / UI symmetry grades (Good–Excellent dual-range). API uses short codes via map below.
const SYMMETRY_VALUES = ["Good", "Very Good", "Excellent"];
const SYMMETRY_DEFAULT_MIN = "Good";
const SYMMETRY_DEFAULT_MAX = "Excellent";
const SYMMETRY_LABEL_TO_API_VALUE = {
  Excellent: "EX",
  "Very Good": "VG",
  Good: "GD",
};

const FLUORESCENCE_PARAM = "fluorescence";
const FLUORESCENCE_MIN_PARAM = "fluorescenceMin";
const FLUORESCENCE_MAX_PARAM = "fluorescenceMax";
// URL / UI fluorescence intensity (Very Strong–None dual-range). API uses short codes via map below.
const FLUORESCENCE_VALUES = ["Very Strong", "Strong", "Medium", "Faint", "None"];
const FLUORESCENCE_DEFAULT_MIN = "Very Strong";
const FLUORESCENCE_DEFAULT_MAX = "None";
// Shopper labels → every Nivoda intensity code that belongs on that step.
// Faint includes Very Slight / Slight; Strong includes STN. Do not expose
// those as extra shopper-visible steps.
const FLUORESCENCE_LABEL_TO_API_VALUES = {
  "Very Strong": ["VST"],
  Strong: ["STG", "STN"],
  Medium: ["MED"],
  Faint: ["VSL", "SLT", "FNT"],
  None: ["NON"],
};

// URL uses certificateLab; backend query uses certificate_lab.
// Shopper-visible chips stay GIA / IGI / GCAL. HRD remains internally
// known so live Nivoda HRD stones are not treated as unknown labs.
const CERTIFICATE_LAB_PARAM = "certificateLab";
const CERTIFICATE_LAB_VALUES = ["GIA", "IGI", "GCAL"];
const CERTIFICATE_LAB_KNOWN_VALUES = ["GIA", "IGI", "HRD", "GCAL"];

// Girdle / Culet (Geometry, multi-select). URL param names match the
// confirmed backend query params (girdle / culet) exactly - see
// ivanov-jewelry-api src/routes/test-diamonds.ts, which comma-splits and
// uppercases whatever is sent. Labels shown to shoppers are GIA-standard
// thickness/culet-size grades; short codes sent to the API are confirmed
// via live Nivoda GraphQL introspection (__type(name: "Girdle")):
// ACT, ETK, VTK, THK, STK, MED, STN, THN, VTN, ETN, ANY. Only the 8
// GIA-standard grades are exposed in the UI (ACT/STN/ANY are Nivoda-only
// extras, not standard GIA girdle grades).
const GIRDLE_PARAM = "girdle";
const GIRDLE_VALUES = [
  "Extremely Thin",
  "Very Thin",
  "Thin",
  "Medium",
  "Slightly Thick",
  "Thick",
  "Very Thick",
  "Extremely Thick",
];
const GIRDLE_LABEL_TO_API_VALUE = {
  "Extremely Thin": "ETN",
  "Very Thin": "VTN",
  Thin: "THN",
  Medium: "MED",
  "Slightly Thick": "STK",
  Thick: "THK",
  "Very Thick": "VTK",
  "Extremely Thick": "ETK",
};

const CULET_PARAM = "culet";
const CULET_VALUES = [
  "None",
  "Very Small",
  "Small",
  "Medium",
  "Slightly Large",
  "Large",
  "Very Large",
  "Extremely Large",
];
// Confirmed via Nivoda GraphQL introspection (__type(name: "CuletOption")):
// NON, VSM, SML, MED, SLG, LGE, VLG, ELG.
const CULET_LABEL_TO_API_VALUE = {
  None: "NON",
  "Very Small": "VSM",
  Small: "SML",
  Medium: "MED",
  "Slightly Large": "SLG",
  Large: "LGE",
  "Very Large": "VLG",
  "Extremely Large": "ELG",
};

const CARAT_MIN_PARAM = "caratMin";
const CARAT_MAX_PARAM = "caratMax";
// Default Exact Range bounds. Matching this range means "no explicit
// Carat filter" in the URL; the server request still sends these bounds.
const CARAT_DEFAULT_MIN = 0.25;
const CARAT_DEFAULT_MAX = 50;

const PRICE_MIN_PARAM = "priceMin";
const PRICE_MAX_PARAM = "priceMax";
// Allowed Budget ($) bounds (matches API dollar_value defaults).
// Empty Min/Max inputs mean "no Price filter" (params absent).
const PRICE_BOUND_MIN = 0;
const PRICE_BOUND_MAX = 2000000;

// Geometry — L/W Ratio, Table %, Depth %, Crown Angle, Pavilion Angle, Star
// Length, Lower Half (dual-range + Min/Max, same architecture as Carat/Price:
// full bound range = no filter). minParam/maxParam match the confirmed
// backend query params exactly (see ivanov-jewelry-api
// src/routes/test-diamonds.ts: ratioMin/Max, tableMin/Max, depthMin/Max,
// crownAngleMin/Max, pavilionAngleMin/Max, starLengthMin/Max,
// lowerHalfMin/Max). key/bridge must match the *Min/*Max element ids and the
// GEOMETRY_RANGE_FILTERS config in sections/diamond-search.liquid.
const GEOMETRY_RANGE_CONFIGS = [
  { key: "ratio", bridge: "Ratio", minParam: "ratioMin", maxParam: "ratioMax", boundMin: 1, boundMax: 2.5, decimals: 2 },
  { key: "table", bridge: "Table", minParam: "tableMin", maxParam: "tableMax", boundMin: 50, boundMax: 75, decimals: 1 },
  { key: "depth", bridge: "Depth", minParam: "depthMin", maxParam: "depthMax", boundMin: 55, boundMax: 75, decimals: 1 },
  { key: "crownAngle", bridge: "CrownAngle", minParam: "crownAngleMin", maxParam: "crownAngleMax", boundMin: 20, boundMax: 40, decimals: 1 },
  { key: "pavilionAngle", bridge: "PavilionAngle", minParam: "pavilionAngleMin", maxParam: "pavilionAngleMax", boundMin: 35, boundMax: 45, decimals: 1 },
  { key: "starLength", bridge: "StarLength", minParam: "starLengthMin", maxParam: "starLengthMax", boundMin: 40, boundMax: 70, decimals: 0 },
  { key: "lowerHalf", bridge: "LowerHalf", minParam: "lowerHalfMin", maxParam: "lowerHalfMax", boundMin: 60, boundMax: 90, decimals: 0 },
];

// Maps a raw IvanovAPI diamond item to the flat shape the existing
// prototype UI already expects (see renderLiveDiamondResults below).
// v360 is real Nivoda data: ivanov-jewelry-api's diamonds_by_query already
// requests certificate.v360 { url, frame_count } (see
// ivanov-jewelry-api/src/services/diamond-search.ts), it was just never
// carried through this flat mapping - not a fake/invented field.
// preparedTurntable is additive backend registry metadata — pass-through
// only (no manifest fetch or prepared-media activation in this mapper).
function mapDiamondItem(item) {
  const certificate = (item && item.diamond && item.diamond.certificate) || {};

  // Keep raw Nivoda certificate untouched. Empty / missing scalars become
  // null so later UI can hide a row instead of inventing a grade.
  const fluorescence =
    certificate.floInt == null || String(certificate.floInt).trim() === ""
      ? null
      : certificate.floInt;
  const fluorescenceColor =
    certificate.floCol == null || String(certificate.floCol).trim() === ""
      ? null
      : certificate.floCol;
  const certificateNumber =
    certificate.certNumber == null || certificate.certNumber === ""
      ? null
      : String(certificate.certNumber);
  const certificateUrl =
    certificate.pdfUrl == null || String(certificate.pdfUrl).trim() === ""
      ? null
      : String(certificate.pdfUrl);

  // Preserve the raw report number even when lab is NONE. Shopper surfaces
  // should use hasGradingReport so a placeholder lab is never treated as a
  // real grading report.
  const labUpper = certificate.lab == null ? "" : String(certificate.lab).trim().toUpperCase();
  const isPlaceholderLab =
    !labUpper ||
    labUpper === "NONE" ||
    labUpper === "N/A" ||
    labUpper === "NA" ||
    labUpper === "UNKNOWN" ||
    labUpper === "-" ||
    labUpper === "\u2014";

  return {
    id: item && item.id,
    // Search returns the raw provider state. Match the backend Detail
    // contract exactly: only exact AVAILABLE is shopper-safe available;
    // every other, missing, or unknown state fails closed.
    availability:
      item && item.diamond && item.diamond.availability === "AVAILABLE"
        ? "available"
        : "unavailable",
    shape: certificate.shape,
    carat: certificate.carats,
    color: certificate.color,
    clarity: certificate.clarity,
    cut: certificate.cut,
    polish: certificate.polish,
    symmetry: certificate.symmetry,
    fluorescence,
    fluorescenceColor,
    certificateNumber,
    certificateUrl,
    hasGradingReport: Boolean(certificateNumber) && !isPlaceholderLab,
    lab: certificate.lab,
    image: certificate.image,
    v360: (certificate.v360 && certificate.v360.url) ? certificate.v360 : null,
    preparedTurntable: (item && item.preparedTurntable) || null,
    // Authoritative Ivanov retail USD from the backend. Do not read
    // Nivoda `price` (delivered cost cents) — that field is not public.
    retailPriceUsd: item && item.retailPriceUsd,
  };
}

function normalizeOriginParam(value) {
  return value === ORIGIN_LAB_GROWN ? ORIGIN_LAB_GROWN : ORIGIN_NATURAL;
}

function getOriginFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    return normalizeOriginParam(params.get(ORIGIN_PARAM));
  } catch (err) {
    return ORIGIN_NATURAL;
  }
}

function getActiveOrigin() {
  const el = document.querySelector("#originOptions button.active");
  const origin = el && el.getAttribute("data-origin");
  return normalizeOriginParam(origin);
}

function getCurrentLabgrown() {
  return getActiveOrigin() === ORIGIN_LAB_GROWN;
}

function setOriginUi(origin) {
  const normalized = normalizeOriginParam(origin);
  document.querySelectorAll("#originOptions button[data-origin]").forEach((button) => {
    const selected = button.getAttribute("data-origin") === normalized;
    button.classList.toggle("active", selected);
    button.setAttribute("aria-pressed", selected ? "true" : "false");
  });
  return normalized;
}

function syncOriginUrl(origin, replace) {
  const normalized = normalizeOriginParam(origin);
  try {
    const url = new URL(window.location.href);
    url.searchParams.set(ORIGIN_PARAM, normalized);
    const next = url.pathname + url.search + url.hash;
    if (replace) {
      history.replaceState(history.state, "", next);
    } else {
      history.pushState(history.state, "", next);
    }
  } catch (err) {
    // Ignore URL sync failures so search still works.
  }
}

function initOriginFromUrl() {
  const origin = getOriginFromUrl();
  setOriginUi(origin);
  // Ensure Natural is explicit in the URL even when the param was missing/invalid.
  syncOriginUrl(origin, true);
  return origin;
}

function shapeSlugToLabel(slug) {
  if (slug == null || slug === "") return null;
  const raw = String(slug).trim();
  if (!raw) return null;
  const lower = raw.toLowerCase();
  if (Object.prototype.hasOwnProperty.call(SHAPE_SLUG_TO_LABEL, lower)) {
    return SHAPE_SLUG_TO_LABEL[lower];
  }
  const mappedSlug = SHAPE_LABEL_TO_SLUG[raw] || SHAPE_LABEL_TO_SLUG[lower];
  return mappedSlug ? SHAPE_SLUG_TO_LABEL[mappedSlug] : null;
}

function shapeLabelToSlug(label) {
  const canonical = shapeSlugToLabel(label);
  return canonical ? SHAPE_LABEL_TO_SLUG[canonical] : null;
}

function isMoreShapeLabel(label) {
  return MORE_SHAPE_LABELS.has(shapeSlugToLabel(label));
}

function syncMoreShapesDisclosure(shapeLabel) {
  const el = document.querySelector("[data-more-shapes]");
  if (!el) return;
  const canonical = shapeSlugToLabel(shapeLabel);
  if (isMoreShapeLabel(canonical)) {
    // Auto-open so a newly selected or URL-restored More Shape is visible.
    // Does not lock the disclosure: the user may collapse it afterward.
    el.open = true;
  } else {
    // Primary selected, no Shape, chip clear, Reset All, or URL restore
    // of a non-More Shape. Manual open with no More Shape selected is
    // left alone only when this function is not called.
    el.open = false;
  }
}

function getRawShapeParamFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.has(SHAPE_PARAM) ? params.get(SHAPE_PARAM) : null;
  } catch (err) {
    return null;
  }
}

function setShapeUi(shapeLabel) {
  const normalized = shapeSlugToLabel(shapeLabel);
  document
    .querySelectorAll(".shape-scroll-container button")
    .forEach((btn) => {
      btn.classList.toggle(
        "active",
        Boolean(normalized) && btn.dataset.shape === normalized
      );
    });
  syncMoreShapesDisclosure(normalized);
  return normalized;
}

function syncShapeUrl(shapeLabel, replace) {
  const slug = shapeLabelToSlug(shapeLabel);
  try {
    const url = new URL(window.location.href);
    const current = url.searchParams.get(SHAPE_PARAM);
    if (slug) {
      if (current === slug) return;
      url.searchParams.set(SHAPE_PARAM, slug);
    } else {
      if (!url.searchParams.has(SHAPE_PARAM)) return;
      url.searchParams.delete(SHAPE_PARAM);
    }
    const next = url.pathname + url.search + url.hash;
    if (replace) {
      history.replaceState(history.state, "", next);
    } else {
      history.pushState(history.state, "", next);
    }
  } catch (err) {
    // Ignore URL sync failures so search still works.
  }
}

function initShapeFromUrl() {
  const raw = getRawShapeParamFromUrl();
  // Missing param → no Shape selected (do not add shape=).
  if (raw == null) {
    setShapeUi(null);
    return null;
  }

  const shape = shapeSlugToLabel(raw);
  if (!shape) {
    // Invalid/empty slug → ignore selection and strip from the URL.
    setShapeUi(null);
    syncShapeUrl(null, true);
    return null;
  }

  setShapeUi(shape);
  // Normalize casing (e.g. Round → round) without creating a history entry.
  syncShapeUrl(shape, true);
  return shape;
}

function colorParamToValue(raw) {
  if (raw == null || raw === "") return null;
  const normalized = String(raw).trim().toUpperCase();
  return COLOR_VALUES.includes(normalized) ? normalized : null;
}

function isDefaultColorRange(min, max) {
  return min === COLOR_DEFAULT_MIN && max === COLOR_DEFAULT_MAX;
}

function normalizeColorRange(min, max) {
  let minIdx = COLOR_VALUES.indexOf(colorParamToValue(min) || "");
  let maxIdx = COLOR_VALUES.indexOf(colorParamToValue(max) || "");
  if (minIdx < 0) minIdx = 0;
  if (maxIdx < 0) maxIdx = COLOR_VALUES.length - 1;
  if (minIdx > maxIdx) {
    const swap = minIdx;
    minIdx = maxIdx;
    maxIdx = swap;
  }
  return { min: COLOR_VALUES[minIdx], max: COLOR_VALUES[maxIdx] };
}

function expandColorRange(min, max) {
  const range = normalizeColorRange(min, max);
  const minIdx = COLOR_VALUES.indexOf(range.min);
  const maxIdx = COLOR_VALUES.indexOf(range.max);
  return COLOR_VALUES.slice(minIdx, maxIdx + 1);
}

function getColorRangeFromUi() {
  const min = colorParamToValue(document.getElementById("colorMin")?.value);
  const max = colorParamToValue(document.getElementById("colorMax")?.value);
  if (!min || !max) return null;
  return normalizeColorRange(min, max);
}

// Returns { kind: "absent"|"invalid"|"ok", min?, max? }.
// Supports color=D (single) and colorMin/colorMax (range).
function readColorParamsFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const hasSingle = params.has(COLOR_PARAM);
    const hasMin = params.has(COLOR_MIN_PARAM);
    const hasMax = params.has(COLOR_MAX_PARAM);

    if (hasMin || hasMax) {
      if (!hasMin || !hasMax) return { kind: "invalid" };
      const min = colorParamToValue(params.get(COLOR_MIN_PARAM));
      const max = colorParamToValue(params.get(COLOR_MAX_PARAM));
      if (!min || !max) return { kind: "invalid" };
      return { kind: "ok", min, max };
    }

    if (hasSingle) {
      const color = colorParamToValue(params.get(COLOR_PARAM));
      if (!color) return { kind: "invalid" };
      return { kind: "ok", min: color, max: color };
    }

    return { kind: "absent" };
  } catch (err) {
    return { kind: "absent" };
  }
}

// Restore dual-range / Min / Max / chip via the liquid bridge when present.
// min/max null → default D–K (no Color filter).
function setColorUi(min, max) {
  if (typeof window.setColorUiFromUrl === "function") {
    window.setColorUiFromUrl(min, max);
    return;
  }
  const minInput = document.getElementById("colorMin");
  const maxInput = document.getElementById("colorMax");
  if (!minInput || !maxInput) return;
  if (min == null || max == null) {
    minInput.value = COLOR_DEFAULT_MIN;
    maxInput.value = COLOR_DEFAULT_MAX;
    return;
  }
  const range = normalizeColorRange(min, max);
  minInput.value = range.min;
  maxInput.value = range.max;
}

// min/max null → clear all color params (default / no Color filter).
// Single grade → color=X. Range → colorMin/colorMax. Never touches other filters.
function syncColorUrl(min, max, replace) {
  try {
    const url = new URL(window.location.href);
    const clear =
      min == null ||
      max == null ||
      !colorParamToValue(min) ||
      !colorParamToValue(max) ||
      isDefaultColorRange(
        colorParamToValue(min),
        colorParamToValue(max)
      );

    if (clear) {
      if (
        !url.searchParams.has(COLOR_PARAM) &&
        !url.searchParams.has(COLOR_MIN_PARAM) &&
        !url.searchParams.has(COLOR_MAX_PARAM)
      ) {
        return;
      }
      url.searchParams.delete(COLOR_PARAM);
      url.searchParams.delete(COLOR_MIN_PARAM);
      url.searchParams.delete(COLOR_MAX_PARAM);
    } else {
      const range = normalizeColorRange(min, max);
      if (range.min === range.max) {
        if (
          url.searchParams.get(COLOR_PARAM) === range.min &&
          !url.searchParams.has(COLOR_MIN_PARAM) &&
          !url.searchParams.has(COLOR_MAX_PARAM)
        ) {
          return;
        }
        url.searchParams.set(COLOR_PARAM, range.min);
        url.searchParams.delete(COLOR_MIN_PARAM);
        url.searchParams.delete(COLOR_MAX_PARAM);
      } else {
        if (
          url.searchParams.get(COLOR_MIN_PARAM) === range.min &&
          url.searchParams.get(COLOR_MAX_PARAM) === range.max &&
          !url.searchParams.has(COLOR_PARAM)
        ) {
          return;
        }
        url.searchParams.set(COLOR_MIN_PARAM, range.min);
        url.searchParams.set(COLOR_MAX_PARAM, range.max);
        url.searchParams.delete(COLOR_PARAM);
      }
    }

    const next = url.pathname + url.search + url.hash;
    if (replace) {
      history.replaceState(history.state, "", next);
    } else {
      history.pushState(history.state, "", next);
    }
  } catch (err) {
    // Ignore URL sync failures so search still works.
  }
}

function initColorFromUrl() {
  const raw = readColorParamsFromUrl();
  if (raw.kind === "absent") {
    setColorUi(null, null);
    return null;
  }
  if (raw.kind === "invalid") {
    setColorUi(null, null);
    syncColorUrl(null, null, true);
    return null;
  }

  const range = normalizeColorRange(raw.min, raw.max);
  if (isDefaultColorRange(range.min, range.max)) {
    setColorUi(null, null);
    syncColorUrl(null, null, true);
    return null;
  }

  setColorUi(range.min, range.max);
  // Normalize casing / single-vs-range form without creating a history entry.
  syncColorUrl(range.min, range.max, true);
  return range;
}

// Coalesce rapid slider/typing updates: first change in a burst pushes a
// history entry; follow-ups replace that entry so Back still works.
let colorUrlSyncReplace = false;
let colorUrlSyncResetTimer = null;
function syncColorUrlFromUi(min, max) {
  syncColorUrl(min, max, colorUrlSyncReplace);
  colorUrlSyncReplace = true;
  if (colorUrlSyncResetTimer) clearTimeout(colorUrlSyncResetTimer);
  colorUrlSyncResetTimer = setTimeout(() => {
    colorUrlSyncReplace = false;
    colorUrlSyncResetTimer = null;
  }, 400);
}

function clarityParamToValue(raw) {
  if (raw == null || raw === "") return null;
  const normalized = String(raw).trim().toUpperCase();
  return CLARITY_VALUES.includes(normalized) ? normalized : null;
}

function isDefaultClarityRange(min, max) {
  return min === CLARITY_DEFAULT_MIN && max === CLARITY_DEFAULT_MAX;
}

function normalizeClarityRange(min, max) {
  let minIdx = CLARITY_VALUES.indexOf(clarityParamToValue(min) || "");
  let maxIdx = CLARITY_VALUES.indexOf(clarityParamToValue(max) || "");
  if (minIdx < 0) minIdx = 0;
  if (maxIdx < 0) maxIdx = CLARITY_VALUES.length - 1;
  if (minIdx > maxIdx) {
    const swap = minIdx;
    minIdx = maxIdx;
    maxIdx = swap;
  }
  return { min: CLARITY_VALUES[minIdx], max: CLARITY_VALUES[maxIdx] };
}

function expandClarityRange(min, max) {
  const range = normalizeClarityRange(min, max);
  const minIdx = CLARITY_VALUES.indexOf(range.min);
  const maxIdx = CLARITY_VALUES.indexOf(range.max);
  return CLARITY_VALUES.slice(minIdx, maxIdx + 1);
}

function getClarityRangeFromUi() {
  const min = clarityParamToValue(document.getElementById("clarityMin")?.value);
  const max = clarityParamToValue(document.getElementById("clarityMax")?.value);
  if (!min || !max) return null;
  return normalizeClarityRange(min, max);
}

// Returns { kind: "absent"|"invalid"|"ok", min?, max? }.
// Supports clarity=VS1 (single) and clarityMin/clarityMax (range).
function readClarityParamsFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const hasSingle = params.has(CLARITY_PARAM);
    const hasMin = params.has(CLARITY_MIN_PARAM);
    const hasMax = params.has(CLARITY_MAX_PARAM);

    if (hasMin || hasMax) {
      if (!hasMin || !hasMax) return { kind: "invalid" };
      const min = clarityParamToValue(params.get(CLARITY_MIN_PARAM));
      const max = clarityParamToValue(params.get(CLARITY_MAX_PARAM));
      if (!min || !max) return { kind: "invalid" };
      return { kind: "ok", min, max };
    }

    if (hasSingle) {
      const clarity = clarityParamToValue(params.get(CLARITY_PARAM));
      if (!clarity) return { kind: "invalid" };
      return { kind: "ok", min: clarity, max: clarity };
    }

    return { kind: "absent" };
  } catch (err) {
    return { kind: "absent" };
  }
}

// Restore dual-range / Min / Max / chip via the liquid bridge when present.
// min/max null → default FL–SI2 (no Clarity filter).
function setClarityUi(min, max) {
  if (typeof window.setClarityUiFromUrl === "function") {
    window.setClarityUiFromUrl(min, max);
    return;
  }
  const minInput = document.getElementById("clarityMin");
  const maxInput = document.getElementById("clarityMax");
  if (!minInput || !maxInput) return;
  if (min == null || max == null) {
    minInput.value = CLARITY_DEFAULT_MIN;
    maxInput.value = CLARITY_DEFAULT_MAX;
    return;
  }
  const range = normalizeClarityRange(min, max);
  minInput.value = range.min;
  maxInput.value = range.max;
}

// min/max null → clear all clarity params (default / no Clarity filter).
// Single grade → clarity=X. Range → clarityMin/clarityMax. Never touches other filters.
function syncClarityUrl(min, max, replace) {
  try {
    const url = new URL(window.location.href);
    const clear =
      min == null ||
      max == null ||
      !clarityParamToValue(min) ||
      !clarityParamToValue(max) ||
      isDefaultClarityRange(
        clarityParamToValue(min),
        clarityParamToValue(max)
      );

    if (clear) {
      if (
        !url.searchParams.has(CLARITY_PARAM) &&
        !url.searchParams.has(CLARITY_MIN_PARAM) &&
        !url.searchParams.has(CLARITY_MAX_PARAM)
      ) {
        return;
      }
      url.searchParams.delete(CLARITY_PARAM);
      url.searchParams.delete(CLARITY_MIN_PARAM);
      url.searchParams.delete(CLARITY_MAX_PARAM);
    } else {
      const range = normalizeClarityRange(min, max);
      if (range.min === range.max) {
        if (
          url.searchParams.get(CLARITY_PARAM) === range.min &&
          !url.searchParams.has(CLARITY_MIN_PARAM) &&
          !url.searchParams.has(CLARITY_MAX_PARAM)
        ) {
          return;
        }
        url.searchParams.set(CLARITY_PARAM, range.min);
        url.searchParams.delete(CLARITY_MIN_PARAM);
        url.searchParams.delete(CLARITY_MAX_PARAM);
      } else {
        if (
          url.searchParams.get(CLARITY_MIN_PARAM) === range.min &&
          url.searchParams.get(CLARITY_MAX_PARAM) === range.max &&
          !url.searchParams.has(CLARITY_PARAM)
        ) {
          return;
        }
        url.searchParams.set(CLARITY_MIN_PARAM, range.min);
        url.searchParams.set(CLARITY_MAX_PARAM, range.max);
        url.searchParams.delete(CLARITY_PARAM);
      }
    }

    const next = url.pathname + url.search + url.hash;
    if (replace) {
      history.replaceState(history.state, "", next);
    } else {
      history.pushState(history.state, "", next);
    }
  } catch (err) {
    // Ignore URL sync failures so search still works.
  }
}

function initClarityFromUrl() {
  const raw = readClarityParamsFromUrl();
  if (raw.kind === "absent") {
    setClarityUi(null, null);
    return null;
  }
  if (raw.kind === "invalid") {
    setClarityUi(null, null);
    syncClarityUrl(null, null, true);
    return null;
  }

  const range = normalizeClarityRange(raw.min, raw.max);
  if (isDefaultClarityRange(range.min, range.max)) {
    setClarityUi(null, null);
    syncClarityUrl(null, null, true);
    return null;
  }

  setClarityUi(range.min, range.max);
  // Normalize casing / single-vs-range form without creating a history entry.
  syncClarityUrl(range.min, range.max, true);
  return range;
}

// Coalesce rapid slider/typing updates: first change in a burst pushes a
// history entry; follow-ups replace that entry so Back still works.
let clarityUrlSyncReplace = false;
let clarityUrlSyncResetTimer = null;
function syncClarityUrlFromUi(min, max) {
  syncClarityUrl(min, max, clarityUrlSyncReplace);
  clarityUrlSyncReplace = true;
  if (clarityUrlSyncResetTimer) clearTimeout(clarityUrlSyncResetTimer);
  clarityUrlSyncResetTimer = setTimeout(() => {
    clarityUrlSyncReplace = false;
    clarityUrlSyncResetTimer = null;
  }, 400);
}

function cutParamToValue(raw) {
  if (raw == null || raw === "") return null;
  const normalized = String(raw).trim().toLowerCase();
  return CUT_VALUES.find((v) => v.toLowerCase() === normalized) || null;
}

function isDefaultCutRange(min, max) {
  return min === CUT_DEFAULT_MIN && max === CUT_DEFAULT_MAX;
}

function normalizeCutRange(min, max) {
  let minIdx = CUT_VALUES.indexOf(cutParamToValue(min) || "");
  let maxIdx = CUT_VALUES.indexOf(cutParamToValue(max) || "");
  if (minIdx < 0) minIdx = 0;
  if (maxIdx < 0) maxIdx = CUT_VALUES.length - 1;
  if (minIdx > maxIdx) {
    const swap = minIdx;
    minIdx = maxIdx;
    maxIdx = swap;
  }
  return { min: CUT_VALUES[minIdx], max: CUT_VALUES[maxIdx] };
}

function expandCutRange(min, max) {
  const range = normalizeCutRange(min, max);
  const minIdx = CUT_VALUES.indexOf(range.min);
  const maxIdx = CUT_VALUES.indexOf(range.max);
  return CUT_VALUES.slice(minIdx, maxIdx + 1);
}

function getCutRangeFromUi() {
  const min = cutParamToValue(document.getElementById("cutMin")?.value);
  const max = cutParamToValue(document.getElementById("cutMax")?.value);
  if (!min || !max) return null;
  return normalizeCutRange(min, max);
}

// Returns { kind: "absent"|"invalid"|"ok", min?, max? }.
// Supports cut=Ideal (single) and cutMin/cutMax (range).
function readCutParamsFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const hasSingle = params.has(CUT_PARAM);
    const hasMin = params.has(CUT_MIN_PARAM);
    const hasMax = params.has(CUT_MAX_PARAM);

    if (hasMin || hasMax) {
      if (!hasMin || !hasMax) return { kind: "invalid" };
      const min = cutParamToValue(params.get(CUT_MIN_PARAM));
      const max = cutParamToValue(params.get(CUT_MAX_PARAM));
      if (!min || !max) return { kind: "invalid" };
      return { kind: "ok", min, max };
    }

    if (hasSingle) {
      const cut = cutParamToValue(params.get(CUT_PARAM));
      if (!cut) return { kind: "invalid" };
      return { kind: "ok", min: cut, max: cut };
    }

    return { kind: "absent" };
  } catch (err) {
    return { kind: "absent" };
  }
}

// Restore dual-range / labels / chip via the liquid bridge when present.
// min/max null → default Fair–Ideal (no Cut filter).
function setCutUi(min, max) {
  if (typeof window.setCutUiFromUrl === "function") {
    window.setCutUiFromUrl(min, max);
    return;
  }
  const minInput = document.getElementById("cutMin");
  const maxInput = document.getElementById("cutMax");
  if (!minInput || !maxInput) return;
  if (min == null || max == null) {
    minInput.value = CUT_DEFAULT_MIN;
    maxInput.value = CUT_DEFAULT_MAX;
    return;
  }
  const range = normalizeCutRange(min, max);
  minInput.value = range.min;
  maxInput.value = range.max;
}

// min/max null → clear all cut params (default / no Cut filter).
// Single grade → cut=X. Range → cutMin/cutMax. Never touches other filters.
function syncCutUrl(min, max, replace) {
  try {
    const url = new URL(window.location.href);
    const clear =
      min == null ||
      max == null ||
      !cutParamToValue(min) ||
      !cutParamToValue(max) ||
      isDefaultCutRange(cutParamToValue(min), cutParamToValue(max));

    if (clear) {
      if (
        !url.searchParams.has(CUT_PARAM) &&
        !url.searchParams.has(CUT_MIN_PARAM) &&
        !url.searchParams.has(CUT_MAX_PARAM)
      ) {
        return;
      }
      url.searchParams.delete(CUT_PARAM);
      url.searchParams.delete(CUT_MIN_PARAM);
      url.searchParams.delete(CUT_MAX_PARAM);
    } else {
      const range = normalizeCutRange(min, max);
      if (range.min === range.max) {
        if (
          url.searchParams.get(CUT_PARAM) === range.min &&
          !url.searchParams.has(CUT_MIN_PARAM) &&
          !url.searchParams.has(CUT_MAX_PARAM)
        ) {
          return;
        }
        url.searchParams.set(CUT_PARAM, range.min);
        url.searchParams.delete(CUT_MIN_PARAM);
        url.searchParams.delete(CUT_MAX_PARAM);
      } else {
        if (
          url.searchParams.get(CUT_MIN_PARAM) === range.min &&
          url.searchParams.get(CUT_MAX_PARAM) === range.max &&
          !url.searchParams.has(CUT_PARAM)
        ) {
          return;
        }
        url.searchParams.set(CUT_MIN_PARAM, range.min);
        url.searchParams.set(CUT_MAX_PARAM, range.max);
        url.searchParams.delete(CUT_PARAM);
      }
    }

    const next = url.pathname + url.search + url.hash;
    if (replace) {
      history.replaceState(history.state, "", next);
    } else {
      history.pushState(history.state, "", next);
    }
  } catch (err) {
    // Ignore URL sync failures so search still works.
  }
}

function initCutFromUrl() {
  const raw = readCutParamsFromUrl();
  if (raw.kind === "absent") {
    setCutUi(null, null);
    return null;
  }
  if (raw.kind === "invalid") {
    setCutUi(null, null);
    syncCutUrl(null, null, true);
    return null;
  }

  const range = normalizeCutRange(raw.min, raw.max);
  if (isDefaultCutRange(range.min, range.max)) {
    setCutUi(null, null);
    syncCutUrl(null, null, true);
    return null;
  }

  setCutUi(range.min, range.max);
  // Normalize casing / single-vs-range form without creating a history entry.
  syncCutUrl(range.min, range.max, true);
  return range;
}

// Coalesce rapid slider updates: first change in a burst pushes a
// history entry; follow-ups replace that entry so Back still works.
let cutUrlSyncReplace = false;
let cutUrlSyncResetTimer = null;
function syncCutUrlFromUi(min, max) {
  syncCutUrl(min, max, cutUrlSyncReplace);
  cutUrlSyncReplace = true;
  if (cutUrlSyncResetTimer) clearTimeout(cutUrlSyncResetTimer);
  cutUrlSyncResetTimer = setTimeout(() => {
    cutUrlSyncReplace = false;
    cutUrlSyncResetTimer = null;
  }, 400);
}

function polishParamToValue(raw) {
  if (raw == null || raw === "") return null;
  const normalized = String(raw).trim().toLowerCase();
  return POLISH_VALUES.find((v) => v.toLowerCase() === normalized) || null;
}

function isDefaultPolishRange(min, max) {
  return min === POLISH_DEFAULT_MIN && max === POLISH_DEFAULT_MAX;
}

function normalizePolishRange(min, max) {
  let minIdx = POLISH_VALUES.indexOf(polishParamToValue(min) || "");
  let maxIdx = POLISH_VALUES.indexOf(polishParamToValue(max) || "");
  if (minIdx < 0) minIdx = 0;
  if (maxIdx < 0) maxIdx = POLISH_VALUES.length - 1;
  if (minIdx > maxIdx) {
    const swap = minIdx;
    minIdx = maxIdx;
    maxIdx = swap;
  }
  return { min: POLISH_VALUES[minIdx], max: POLISH_VALUES[maxIdx] };
}

function expandPolishRange(min, max) {
  const range = normalizePolishRange(min, max);
  const minIdx = POLISH_VALUES.indexOf(range.min);
  const maxIdx = POLISH_VALUES.indexOf(range.max);
  return POLISH_VALUES.slice(minIdx, maxIdx + 1);
}

function getPolishRangeFromUi() {
  const min = polishParamToValue(document.getElementById("polishMin")?.value);
  const max = polishParamToValue(document.getElementById("polishMax")?.value);
  if (!min || !max) return null;
  return normalizePolishRange(min, max);
}

// Returns { kind: "absent"|"invalid"|"ok", min?, max? }.
// Supports polish=Excellent (single) and polishMin/polishMax (range).
function readPolishParamsFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const hasSingle = params.has(POLISH_PARAM);
    const hasMin = params.has(POLISH_MIN_PARAM);
    const hasMax = params.has(POLISH_MAX_PARAM);

    if (hasMin || hasMax) {
      if (!hasMin || !hasMax) return { kind: "invalid" };
      const min = polishParamToValue(params.get(POLISH_MIN_PARAM));
      const max = polishParamToValue(params.get(POLISH_MAX_PARAM));
      if (!min || !max) return { kind: "invalid" };
      return { kind: "ok", min, max };
    }

    if (hasSingle) {
      const polish = polishParamToValue(params.get(POLISH_PARAM));
      if (!polish) return { kind: "invalid" };
      return { kind: "ok", min: polish, max: polish };
    }

    return { kind: "absent" };
  } catch (err) {
    return { kind: "absent" };
  }
}

// Restore dual-range / labels / chip via the liquid bridge when present.
// min/max null → default Good–Excellent (no Polish filter).
function setPolishUi(min, max) {
  if (typeof window.setPolishUiFromUrl === "function") {
    window.setPolishUiFromUrl(min, max);
    return;
  }
  const minInput = document.getElementById("polishMin");
  const maxInput = document.getElementById("polishMax");
  if (!minInput || !maxInput) return;
  if (min == null || max == null) {
    minInput.value = POLISH_DEFAULT_MIN;
    maxInput.value = POLISH_DEFAULT_MAX;
    return;
  }
  const range = normalizePolishRange(min, max);
  minInput.value = range.min;
  maxInput.value = range.max;
}

// min/max null → clear all polish params (default / no Polish filter).
// Single grade → polish=X. Range → polishMin/polishMax. Never touches other filters.
function syncPolishUrl(min, max, replace) {
  try {
    const url = new URL(window.location.href);
    const clear =
      min == null ||
      max == null ||
      !polishParamToValue(min) ||
      !polishParamToValue(max) ||
      isDefaultPolishRange(polishParamToValue(min), polishParamToValue(max));

    if (clear) {
      if (
        !url.searchParams.has(POLISH_PARAM) &&
        !url.searchParams.has(POLISH_MIN_PARAM) &&
        !url.searchParams.has(POLISH_MAX_PARAM)
      ) {
        return;
      }
      url.searchParams.delete(POLISH_PARAM);
      url.searchParams.delete(POLISH_MIN_PARAM);
      url.searchParams.delete(POLISH_MAX_PARAM);
    } else {
      const range = normalizePolishRange(min, max);
      if (range.min === range.max) {
        if (
          url.searchParams.get(POLISH_PARAM) === range.min &&
          !url.searchParams.has(POLISH_MIN_PARAM) &&
          !url.searchParams.has(POLISH_MAX_PARAM)
        ) {
          return;
        }
        url.searchParams.set(POLISH_PARAM, range.min);
        url.searchParams.delete(POLISH_MIN_PARAM);
        url.searchParams.delete(POLISH_MAX_PARAM);
      } else {
        if (
          url.searchParams.get(POLISH_MIN_PARAM) === range.min &&
          url.searchParams.get(POLISH_MAX_PARAM) === range.max &&
          !url.searchParams.has(POLISH_PARAM)
        ) {
          return;
        }
        url.searchParams.set(POLISH_MIN_PARAM, range.min);
        url.searchParams.set(POLISH_MAX_PARAM, range.max);
        url.searchParams.delete(POLISH_PARAM);
      }
    }

    const next = url.pathname + url.search + url.hash;
    if (replace) {
      history.replaceState(history.state, "", next);
    } else {
      history.pushState(history.state, "", next);
    }
  } catch (err) {
    // Ignore URL sync failures so search still works.
  }
}

function initPolishFromUrl() {
  const raw = readPolishParamsFromUrl();
  if (raw.kind === "absent") {
    setPolishUi(null, null);
    return null;
  }
  if (raw.kind === "invalid") {
    setPolishUi(null, null);
    syncPolishUrl(null, null, true);
    return null;
  }

  const range = normalizePolishRange(raw.min, raw.max);
  if (isDefaultPolishRange(range.min, range.max)) {
    setPolishUi(null, null);
    syncPolishUrl(null, null, true);
    return null;
  }

  setPolishUi(range.min, range.max);
  // Normalize casing / single-vs-range form without creating a history entry.
  syncPolishUrl(range.min, range.max, true);
  return range;
}

// Coalesce rapid slider updates: first change in a burst pushes a
// history entry; follow-ups replace that entry so Back still works.
let polishUrlSyncReplace = false;
let polishUrlSyncResetTimer = null;
function syncPolishUrlFromUi(min, max) {
  syncPolishUrl(min, max, polishUrlSyncReplace);
  polishUrlSyncReplace = true;
  if (polishUrlSyncResetTimer) clearTimeout(polishUrlSyncResetTimer);
  polishUrlSyncResetTimer = setTimeout(() => {
    polishUrlSyncReplace = false;
    polishUrlSyncResetTimer = null;
  }, 400);
}

function symmetryParamToValue(raw) {
  if (raw == null || raw === "") return null;
  const normalized = String(raw).trim().toLowerCase();
  return SYMMETRY_VALUES.find((v) => v.toLowerCase() === normalized) || null;
}

function isDefaultSymmetryRange(min, max) {
  return min === SYMMETRY_DEFAULT_MIN && max === SYMMETRY_DEFAULT_MAX;
}

function normalizeSymmetryRange(min, max) {
  let minIdx = SYMMETRY_VALUES.indexOf(symmetryParamToValue(min) || "");
  let maxIdx = SYMMETRY_VALUES.indexOf(symmetryParamToValue(max) || "");
  if (minIdx < 0) minIdx = 0;
  if (maxIdx < 0) maxIdx = SYMMETRY_VALUES.length - 1;
  if (minIdx > maxIdx) {
    const swap = minIdx;
    minIdx = maxIdx;
    maxIdx = swap;
  }
  return { min: SYMMETRY_VALUES[minIdx], max: SYMMETRY_VALUES[maxIdx] };
}

function expandSymmetryRange(min, max) {
  const range = normalizeSymmetryRange(min, max);
  const minIdx = SYMMETRY_VALUES.indexOf(range.min);
  const maxIdx = SYMMETRY_VALUES.indexOf(range.max);
  return SYMMETRY_VALUES.slice(minIdx, maxIdx + 1);
}

function getSymmetryRangeFromUi() {
  const min = symmetryParamToValue(document.getElementById("symmetryMin")?.value);
  const max = symmetryParamToValue(document.getElementById("symmetryMax")?.value);
  if (!min || !max) return null;
  return normalizeSymmetryRange(min, max);
}

// Returns { kind: "absent"|"invalid"|"ok", min?, max? }.
// Supports symmetry=Excellent (single) and symmetryMin/symmetryMax (range).
function readSymmetryParamsFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const hasSingle = params.has(SYMMETRY_PARAM);
    const hasMin = params.has(SYMMETRY_MIN_PARAM);
    const hasMax = params.has(SYMMETRY_MAX_PARAM);

    if (hasMin || hasMax) {
      if (!hasMin || !hasMax) return { kind: "invalid" };
      const min = symmetryParamToValue(params.get(SYMMETRY_MIN_PARAM));
      const max = symmetryParamToValue(params.get(SYMMETRY_MAX_PARAM));
      if (!min || !max) return { kind: "invalid" };
      return { kind: "ok", min, max };
    }

    if (hasSingle) {
      const symmetry = symmetryParamToValue(params.get(SYMMETRY_PARAM));
      if (!symmetry) return { kind: "invalid" };
      return { kind: "ok", min: symmetry, max: symmetry };
    }

    return { kind: "absent" };
  } catch (err) {
    return { kind: "absent" };
  }
}

// Restore dual-range / labels / chip via the liquid bridge when present.
// min/max null → default Good–Excellent (no Symmetry filter).
function setSymmetryUi(min, max) {
  if (typeof window.setSymmetryUiFromUrl === "function") {
    window.setSymmetryUiFromUrl(min, max);
    return;
  }
  const minInput = document.getElementById("symmetryMin");
  const maxInput = document.getElementById("symmetryMax");
  if (!minInput || !maxInput) return;
  if (min == null || max == null) {
    minInput.value = SYMMETRY_DEFAULT_MIN;
    maxInput.value = SYMMETRY_DEFAULT_MAX;
    return;
  }
  const range = normalizeSymmetryRange(min, max);
  minInput.value = range.min;
  maxInput.value = range.max;
}

// min/max null → clear all symmetry params (default / no Symmetry filter).
// Single grade → symmetry=X. Range → symmetryMin/symmetryMax. Never touches other filters.
function syncSymmetryUrl(min, max, replace) {
  try {
    const url = new URL(window.location.href);
    const clear =
      min == null ||
      max == null ||
      !symmetryParamToValue(min) ||
      !symmetryParamToValue(max) ||
      isDefaultSymmetryRange(symmetryParamToValue(min), symmetryParamToValue(max));

    if (clear) {
      if (
        !url.searchParams.has(SYMMETRY_PARAM) &&
        !url.searchParams.has(SYMMETRY_MIN_PARAM) &&
        !url.searchParams.has(SYMMETRY_MAX_PARAM)
      ) {
        return;
      }
      url.searchParams.delete(SYMMETRY_PARAM);
      url.searchParams.delete(SYMMETRY_MIN_PARAM);
      url.searchParams.delete(SYMMETRY_MAX_PARAM);
    } else {
      const range = normalizeSymmetryRange(min, max);
      if (range.min === range.max) {
        if (
          url.searchParams.get(SYMMETRY_PARAM) === range.min &&
          !url.searchParams.has(SYMMETRY_MIN_PARAM) &&
          !url.searchParams.has(SYMMETRY_MAX_PARAM)
        ) {
          return;
        }
        url.searchParams.set(SYMMETRY_PARAM, range.min);
        url.searchParams.delete(SYMMETRY_MIN_PARAM);
        url.searchParams.delete(SYMMETRY_MAX_PARAM);
      } else {
        if (
          url.searchParams.get(SYMMETRY_MIN_PARAM) === range.min &&
          url.searchParams.get(SYMMETRY_MAX_PARAM) === range.max &&
          !url.searchParams.has(SYMMETRY_PARAM)
        ) {
          return;
        }
        url.searchParams.set(SYMMETRY_MIN_PARAM, range.min);
        url.searchParams.set(SYMMETRY_MAX_PARAM, range.max);
        url.searchParams.delete(SYMMETRY_PARAM);
      }
    }

    const next = url.pathname + url.search + url.hash;
    if (replace) {
      history.replaceState(history.state, "", next);
    } else {
      history.pushState(history.state, "", next);
    }
  } catch (err) {
    // Ignore URL sync failures so search still works.
  }
}

function initSymmetryFromUrl() {
  const raw = readSymmetryParamsFromUrl();
  if (raw.kind === "absent") {
    setSymmetryUi(null, null);
    return null;
  }
  if (raw.kind === "invalid") {
    setSymmetryUi(null, null);
    syncSymmetryUrl(null, null, true);
    return null;
  }

  const range = normalizeSymmetryRange(raw.min, raw.max);
  if (isDefaultSymmetryRange(range.min, range.max)) {
    setSymmetryUi(null, null);
    syncSymmetryUrl(null, null, true);
    return null;
  }

  setSymmetryUi(range.min, range.max);
  // Normalize casing / single-vs-range form without creating a history entry.
  syncSymmetryUrl(range.min, range.max, true);
  return range;
}

// Coalesce rapid slider updates: first change in a burst pushes a
// history entry; follow-ups replace that entry so Back still works.
let symmetryUrlSyncReplace = false;
let symmetryUrlSyncResetTimer = null;
function syncSymmetryUrlFromUi(min, max) {
  syncSymmetryUrl(min, max, symmetryUrlSyncReplace);
  symmetryUrlSyncReplace = true;
  if (symmetryUrlSyncResetTimer) clearTimeout(symmetryUrlSyncResetTimer);
  symmetryUrlSyncResetTimer = setTimeout(() => {
    symmetryUrlSyncReplace = false;
    symmetryUrlSyncResetTimer = null;
  }, 400);
}

function fluorescenceParamToValue(raw) {
  if (raw == null || raw === "") return null;
  const normalized = String(raw).trim().toLowerCase();
  return FLUORESCENCE_VALUES.find((v) => v.toLowerCase() === normalized) || null;
}

function isDefaultFluorescenceRange(min, max) {
  return min === FLUORESCENCE_DEFAULT_MIN && max === FLUORESCENCE_DEFAULT_MAX;
}

function normalizeFluorescenceRange(min, max) {
  let minIdx = FLUORESCENCE_VALUES.indexOf(fluorescenceParamToValue(min) || "");
  let maxIdx = FLUORESCENCE_VALUES.indexOf(fluorescenceParamToValue(max) || "");
  if (minIdx < 0) minIdx = 0;
  if (maxIdx < 0) maxIdx = FLUORESCENCE_VALUES.length - 1;
  if (minIdx > maxIdx) {
    const swap = minIdx;
    minIdx = maxIdx;
    maxIdx = swap;
  }
  return { min: FLUORESCENCE_VALUES[minIdx], max: FLUORESCENCE_VALUES[maxIdx] };
}

function expandFluorescenceRange(min, max) {
  const range = normalizeFluorescenceRange(min, max);
  const minIdx = FLUORESCENCE_VALUES.indexOf(range.min);
  const maxIdx = FLUORESCENCE_VALUES.indexOf(range.max);
  return FLUORESCENCE_VALUES.slice(minIdx, maxIdx + 1);
}

function fluorescenceRangeToApiValues(min, max) {
  const codes = [];
  const seen = new Set();
  expandFluorescenceRange(min, max).forEach((label) => {
    const values = FLUORESCENCE_LABEL_TO_API_VALUES[label] || [];
    values.forEach((code) => {
      if (!seen.has(code)) {
        seen.add(code);
        codes.push(code);
      }
    });
  });
  return codes;
}

function getFluorescenceRangeFromUi() {
  const min = fluorescenceParamToValue(document.getElementById("fluorescenceMin")?.value);
  const max = fluorescenceParamToValue(document.getElementById("fluorescenceMax")?.value);
  if (!min || !max) return null;
  return normalizeFluorescenceRange(min, max);
}

// Returns { kind: "absent"|"invalid"|"ok", min?, max? }.
// Supports fluorescence=None (single) and fluorescenceMin/fluorescenceMax (range).
function readFluorescenceParamsFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const hasSingle = params.has(FLUORESCENCE_PARAM);
    const hasMin = params.has(FLUORESCENCE_MIN_PARAM);
    const hasMax = params.has(FLUORESCENCE_MAX_PARAM);

    if (hasMin || hasMax) {
      if (!hasMin || !hasMax) return { kind: "invalid" };
      const min = fluorescenceParamToValue(params.get(FLUORESCENCE_MIN_PARAM));
      const max = fluorescenceParamToValue(params.get(FLUORESCENCE_MAX_PARAM));
      if (!min || !max) return { kind: "invalid" };
      return { kind: "ok", min, max };
    }

    if (hasSingle) {
      const fluorescence = fluorescenceParamToValue(params.get(FLUORESCENCE_PARAM));
      if (!fluorescence) return { kind: "invalid" };
      return { kind: "ok", min: fluorescence, max: fluorescence };
    }

    return { kind: "absent" };
  } catch (err) {
    return { kind: "absent" };
  }
}

// Restore dual-range / labels / chip via the liquid bridge when present.
// min/max null → default Very Strong–None (no Fluorescence filter).
function setFluorescenceUi(min, max) {
  if (typeof window.setFluorescenceUiFromUrl === "function") {
    window.setFluorescenceUiFromUrl(min, max);
    return;
  }
  const minInput = document.getElementById("fluorescenceMin");
  const maxInput = document.getElementById("fluorescenceMax");
  if (!minInput || !maxInput) return;
  if (min == null || max == null) {
    minInput.value = FLUORESCENCE_DEFAULT_MIN;
    maxInput.value = FLUORESCENCE_DEFAULT_MAX;
    return;
  }
  const range = normalizeFluorescenceRange(min, max);
  minInput.value = range.min;
  maxInput.value = range.max;
}

// min/max null → clear all fluorescence params (default / no Fluorescence filter).
// Single intensity → fluorescence=X. Range → fluorescenceMin/fluorescenceMax. Never touches other filters.
function syncFluorescenceUrl(min, max, replace) {
  try {
    const url = new URL(window.location.href);
    const clear =
      min == null ||
      max == null ||
      !fluorescenceParamToValue(min) ||
      !fluorescenceParamToValue(max) ||
      isDefaultFluorescenceRange(fluorescenceParamToValue(min), fluorescenceParamToValue(max));

    if (clear) {
      if (
        !url.searchParams.has(FLUORESCENCE_PARAM) &&
        !url.searchParams.has(FLUORESCENCE_MIN_PARAM) &&
        !url.searchParams.has(FLUORESCENCE_MAX_PARAM)
      ) {
        return;
      }
      url.searchParams.delete(FLUORESCENCE_PARAM);
      url.searchParams.delete(FLUORESCENCE_MIN_PARAM);
      url.searchParams.delete(FLUORESCENCE_MAX_PARAM);
    } else {
      const range = normalizeFluorescenceRange(min, max);
      if (range.min === range.max) {
        if (
          url.searchParams.get(FLUORESCENCE_PARAM) === range.min &&
          !url.searchParams.has(FLUORESCENCE_MIN_PARAM) &&
          !url.searchParams.has(FLUORESCENCE_MAX_PARAM)
        ) {
          return;
        }
        url.searchParams.set(FLUORESCENCE_PARAM, range.min);
        url.searchParams.delete(FLUORESCENCE_MIN_PARAM);
        url.searchParams.delete(FLUORESCENCE_MAX_PARAM);
      } else {
        if (
          url.searchParams.get(FLUORESCENCE_MIN_PARAM) === range.min &&
          url.searchParams.get(FLUORESCENCE_MAX_PARAM) === range.max &&
          !url.searchParams.has(FLUORESCENCE_PARAM)
        ) {
          return;
        }
        url.searchParams.set(FLUORESCENCE_MIN_PARAM, range.min);
        url.searchParams.set(FLUORESCENCE_MAX_PARAM, range.max);
        url.searchParams.delete(FLUORESCENCE_PARAM);
      }
    }

    const next = url.pathname + url.search + url.hash;
    if (replace) {
      history.replaceState(history.state, "", next);
    } else {
      history.pushState(history.state, "", next);
    }
  } catch (err) {
    // Ignore URL sync failures so search still works.
  }
}

function initFluorescenceFromUrl() {
  const raw = readFluorescenceParamsFromUrl();
  if (raw.kind === "absent") {
    setFluorescenceUi(null, null);
    return null;
  }
  if (raw.kind === "invalid") {
    setFluorescenceUi(null, null);
    syncFluorescenceUrl(null, null, true);
    return null;
  }

  const range = normalizeFluorescenceRange(raw.min, raw.max);
  if (isDefaultFluorescenceRange(range.min, range.max)) {
    setFluorescenceUi(null, null);
    syncFluorescenceUrl(null, null, true);
    return null;
  }

  setFluorescenceUi(range.min, range.max);
  // Normalize casing / single-vs-range form without creating a history entry.
  syncFluorescenceUrl(range.min, range.max, true);
  return range;
}

// Coalesce rapid slider updates: first change in a burst pushes a
// history entry; follow-ups replace that entry so Back still works.
let fluorescenceUrlSyncReplace = false;
let fluorescenceUrlSyncResetTimer = null;
function syncFluorescenceUrlFromUi(min, max) {
  syncFluorescenceUrl(min, max, fluorescenceUrlSyncReplace);
  fluorescenceUrlSyncReplace = true;
  if (fluorescenceUrlSyncResetTimer) clearTimeout(fluorescenceUrlSyncResetTimer);
  fluorescenceUrlSyncResetTimer = setTimeout(() => {
    fluorescenceUrlSyncReplace = false;
    fluorescenceUrlSyncResetTimer = null;
  }, 400);
}

function normalizeKnownCertificateLabValue(value) {
  if (value == null || value === "") return null;
  const normalized = String(value).trim().toUpperCase();
  return CERTIFICATE_LAB_KNOWN_VALUES.includes(normalized) ? normalized : null;
}

function normalizeCertificateLabValue(value) {
  if (value == null || value === "") return null;
  const normalized = String(value).trim().toUpperCase();
  return CERTIFICATE_LAB_VALUES.includes(normalized) ? normalized : null;
}

// Parse comma-separated labs; keep only shopper-visible values; stable
// CERTIFICATE_LAB_VALUES order. HRD is recognized by
// normalizeKnownCertificateLabValue but is not a shopper filter.
function normalizeCertificateLabList(values) {
  const selected = new Set();
  (Array.isArray(values) ? values : []).forEach((value) => {
    const lab = normalizeCertificateLabValue(value);
    if (lab) selected.add(lab);
  });
  return CERTIFICATE_LAB_VALUES.filter((lab) => selected.has(lab));
}

function parseCertificateLabParam(raw) {
  if (raw == null) return [];
  const parts = String(raw)
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  return normalizeCertificateLabList(parts);
}

function getCertificateLabsFromUi() {
  const selected = Array.from(
    document.querySelectorAll("#labOptions span.active[data-certificate-lab]")
  ).map((span) => span.getAttribute("data-certificate-lab"));
  return normalizeCertificateLabList(selected);
}

function getCertificateLabsFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (!params.has(CERTIFICATE_LAB_PARAM)) return { kind: "absent", labs: [] };
    const raw = params.get(CERTIFICATE_LAB_PARAM);
    if (raw == null || String(raw).trim() === "") {
      return { kind: "invalid", labs: [] };
    }
    const labs = parseCertificateLabParam(raw);
    if (!labs.length) return { kind: "invalid", labs: [] };
    return { kind: "ok", labs };
  } catch (err) {
    return { kind: "absent", labs: [] };
  }
}

function setCertificateLabUi(labs) {
  const selected = new Set(normalizeCertificateLabList(labs));
  document.querySelectorAll("#labOptions span[data-certificate-lab]").forEach((span) => {
    const lab = normalizeCertificateLabValue(span.getAttribute("data-certificate-lab"));
    span.classList.toggle("active", Boolean(lab) && selected.has(lab));
  });
  return Array.from(selected);
}

// labs empty/null → clear certificateLab. Never touches other filters.
function syncCertificateLabUrl(labs, replace) {
  const normalized = normalizeCertificateLabList(labs);
  const nextValue = normalized.join(",");
  try {
    const url = new URL(window.location.href);
    const current = url.searchParams.get(CERTIFICATE_LAB_PARAM);
    if (!normalized.length) {
      if (!url.searchParams.has(CERTIFICATE_LAB_PARAM)) return;
      url.searchParams.delete(CERTIFICATE_LAB_PARAM);
    } else {
      if (current === nextValue) return;
      url.searchParams.set(CERTIFICATE_LAB_PARAM, nextValue);
    }
    const next = url.pathname + url.search + url.hash;
    if (replace) {
      history.replaceState(history.state, "", next);
    } else {
      history.pushState(history.state, "", next);
    }
  } catch (err) {
    // Ignore URL sync failures so search still works.
  }
}

function initCertificateLabFromUrl() {
  const raw = getCertificateLabsFromUrl();
  if (raw.kind === "absent") {
    setCertificateLabUi([]);
    return [];
  }
  if (raw.kind === "invalid") {
    setCertificateLabUi([]);
    syncCertificateLabUrl([], true);
    return [];
  }
  setCertificateLabUi(raw.labs);
  // Normalize casing / ordering without creating a history entry.
  syncCertificateLabUrl(raw.labs, true);
  return raw.labs;
}

/** Builds the shared multi-select (Certificate-Lab-style) controller used by
 * Girdle and Culet: normalize/get/set/sync/init, parameterized by URL param,
 * allowed label list and the #<optionsId> span[data-<attr>] markup. */
function createMultiSelectController(config) {
  function normalizeValue(value) {
    if (value == null || value === "") return null;
    const normalized = String(value).trim().toLowerCase();
    return (
      config.values.find((v) => v.toLowerCase() === normalized) || null
    );
  }

  function normalizeList(values) {
    const selected = new Set();
    (Array.isArray(values) ? values : []).forEach((value) => {
      const label = normalizeValue(value);
      if (label) selected.add(label);
    });
    return config.values.filter((label) => selected.has(label));
  }

  function parseParam(raw) {
    if (raw == null) return [];
    const parts = String(raw)
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
    return normalizeList(parts);
  }

  function getFromUi() {
    const selected = Array.from(
      document.querySelectorAll(`#${config.optionsId} span.active[data-${config.attr}]`)
    ).map((span) => span.getAttribute(`data-${config.attr}`));
    return normalizeList(selected);
  }

  function getFromUrl() {
    try {
      const params = new URLSearchParams(window.location.search);
      if (!params.has(config.param)) return { kind: "absent", labels: [] };
      const raw = params.get(config.param);
      if (raw == null || String(raw).trim() === "") {
        return { kind: "invalid", labels: [] };
      }
      const labels = parseParam(raw);
      if (!labels.length) return { kind: "invalid", labels: [] };
      return { kind: "ok", labels };
    } catch (err) {
      return { kind: "absent", labels: [] };
    }
  }

  function setUi(labels) {
    const selected = new Set(normalizeList(labels));
    document.querySelectorAll(`#${config.optionsId} span[data-${config.attr}]`).forEach((span) => {
      const label = normalizeValue(span.getAttribute(`data-${config.attr}`));
      span.classList.toggle("active", Boolean(label) && selected.has(label));
    });
    return Array.from(selected);
  }

  // labels empty/null → clear the param. Never touches other filters.
  function syncUrl(labels, replace) {
    const normalized = normalizeList(labels);
    const nextValue = normalized.join(",");
    try {
      const url = new URL(window.location.href);
      const current = url.searchParams.get(config.param);
      if (!normalized.length) {
        if (!url.searchParams.has(config.param)) return;
        url.searchParams.delete(config.param);
      } else {
        if (current === nextValue) return;
        url.searchParams.set(config.param, nextValue);
      }
      const next = url.pathname + url.search + url.hash;
      if (replace) {
        history.replaceState(history.state, "", next);
      } else {
        history.pushState(history.state, "", next);
      }
    } catch (err) {
      // Ignore URL sync failures so search still works.
    }
  }

  function initFromUrl() {
    const raw = getFromUrl();
    if (raw.kind === "absent") {
      setUi([]);
      return [];
    }
    if (raw.kind === "invalid") {
      setUi([]);
      syncUrl([], true);
      return [];
    }
    setUi(raw.labels);
    // Normalize casing / ordering without creating a history entry.
    syncUrl(raw.labels, true);
    return raw.labels;
  }

  return { getFromUi, getFromUrl, setUi, syncUrl, initFromUrl };
}

const girdleController = createMultiSelectController({
  param: GIRDLE_PARAM,
  values: GIRDLE_VALUES,
  optionsId: "girdleOptions",
  attr: "girdle",
});
const getGirdleFromUi = girdleController.getFromUi;
const getGirdleFromUrl = girdleController.getFromUrl;
const setGirdleUi = girdleController.setUi;
const syncGirdleUrl = girdleController.syncUrl;
const initGirdleFromUrl = girdleController.initFromUrl;

const culetController = createMultiSelectController({
  param: CULET_PARAM,
  values: CULET_VALUES,
  optionsId: "culetOptions",
  attr: "culet",
});
const getCuletFromUi = culetController.getFromUi;
const getCuletFromUrl = culetController.getFromUrl;
const setCuletUi = culetController.setUi;
const syncCuletUrl = culetController.syncUrl;
const initCuletFromUrl = culetController.initFromUrl;

// Media (More Filters) — Images / 360° / Certificate PDF. Three fully
// independent boolean toggles; each maps 1:1 to a confirmed backend field.
// 360° and Certificate PDF: URL param is the literal "true" when ON;
// OFF means the param is simply absent (never "false").
// Images is a product default ON: absent hasImage = ON (clean URL) and
// the request sends has_image=true. Explicit OFF writes hasImage=false
// and omits has_image from the API request — never send has_image=false.
// There is NO standalone Video filter - do not add filters.has_video
// or a "video" param.
const MEDIA_HAS_IMAGE_PARAM = "hasImage"; // -> backend has_image
const MEDIA_HAS_V360_PARAM = "hasV360"; // -> backend has_v360
const MEDIA_HAS_CERTPDF_PARAM = "hasCertPdf"; // -> backend has_certpdf

/** Builds a single independent boolean-toggle controller (Media-style).
 * Default-OFF toggles (360°, Certificate PDF, Availability): URL param is
 * the literal "true" when ON; absent === OFF. Never write "false".
 * defaultOn:true (Images only): absent === product default ON (clean URL);
 * explicit OFF writes the literal "false"; legacy "true" is stripped back
 * to a clean URL. Do not set defaultOn on any other toggle. Mirrors
 * createMultiSelectController's shape so callers — restoreFiltersFromUrl,
 * initListeners, collectActiveServerFilters — wire each toggle the same way. */
function createBooleanToggleController(config) {
  const defaultOn = config.defaultOn === true;

  function getFromUi() {
    const el = document.getElementById(config.elementId);
    return Boolean(el && el.checked);
  }

  function getFromUrl() {
    try {
      const params = new URLSearchParams(window.location.search);
      if (!params.has(config.param)) {
        return { kind: "absent", value: defaultOn };
      }
      const raw = String(params.get(config.param)).trim().toLowerCase();
      if (defaultOn) {
        if (raw === "false") return { kind: "ok", value: false };
        if (raw === "true") return { kind: "ok", value: true };
        return { kind: "invalid", value: true };
      }
      if (raw === "true") return { kind: "ok", value: true };
      return { kind: "invalid", value: false };
    } catch (err) {
      return { kind: "absent", value: defaultOn };
    }
  }

  function setUi(value) {
    const el = document.getElementById(config.elementId);
    if (el) el.checked = Boolean(value);
    return Boolean(value);
  }

  // Default-OFF: falsy removes this toggle's own param; truthy writes "true".
  // Default-ON (Images): truthy removes the param (clean URL / product
  // default); falsy writes "false". Never touches other filters.
  function syncUrl(value, replace) {
    try {
      const url = new URL(window.location.href);
      const has = url.searchParams.has(config.param);
      if (defaultOn) {
        if (value) {
          if (!has) return;
          url.searchParams.delete(config.param);
        } else {
          const current = has
            ? String(url.searchParams.get(config.param)).trim().toLowerCase()
            : "";
          if (current === "false") return;
          url.searchParams.set(config.param, "false");
        }
      } else if (!value) {
        if (!has) return;
        url.searchParams.delete(config.param);
      } else {
        if (has && url.searchParams.get(config.param) === "true") return;
        url.searchParams.set(config.param, "true");
      }
      const next = url.pathname + url.search + url.hash;
      if (replace) {
        history.replaceState(history.state, "", next);
      } else {
        history.pushState(history.state, "", next);
      }
    } catch (err) {
      // Ignore URL sync failures so search still works.
    }
  }

  function initFromUrl() {
    const raw = getFromUrl();
    if (raw.kind === "absent") {
      setUi(raw.value);
      return raw.value;
    }
    setUi(raw.value);
    syncUrl(raw.value, true);
    return raw.value;
  }

  return { getFromUi, getFromUrl, setUi, syncUrl, initFromUrl };
}

const mediaHasImageController = createBooleanToggleController({
  param: MEDIA_HAS_IMAGE_PARAM,
  elementId: "mediaHasImage",
  defaultOn: true,
});
const getMediaHasImageFromUi = mediaHasImageController.getFromUi;
const getMediaHasImageFromUrl = mediaHasImageController.getFromUrl;
const setMediaHasImageUi = mediaHasImageController.setUi;
const syncMediaHasImageUrl = mediaHasImageController.syncUrl;
const initMediaHasImageFromUrl = mediaHasImageController.initFromUrl;

const mediaHasV360Controller = createBooleanToggleController({
  param: MEDIA_HAS_V360_PARAM,
  elementId: "mediaHasV360",
});
const getMediaHasV360FromUi = mediaHasV360Controller.getFromUi;
const getMediaHasV360FromUrl = mediaHasV360Controller.getFromUrl;
const setMediaHasV360Ui = mediaHasV360Controller.setUi;
const syncMediaHasV360Url = mediaHasV360Controller.syncUrl;
const initMediaHasV360FromUrl = mediaHasV360Controller.initFromUrl;

const mediaHasCertPdfController = createBooleanToggleController({
  param: MEDIA_HAS_CERTPDF_PARAM,
  elementId: "mediaHasCertPdf",
});
const getMediaHasCertPdfFromUi = mediaHasCertPdfController.getFromUi;
const getMediaHasCertPdfFromUrl = mediaHasCertPdfController.getFromUrl;
const setMediaHasCertPdfUi = mediaHasCertPdfController.setUi;
const syncMediaHasCertPdfUrl = mediaHasCertPdfController.syncUrl;
const initMediaHasCertPdfFromUrl = mediaHasCertPdfController.initFromUrl;

// Availability (More Filters) — Faster Fulfillment / Returnable / Traced.
// Three fully independent boolean toggles, same createBooleanToggleController
// shape as Media's Images/360°/Certificate PDF; each maps 1:1 to a confirmed
// backend field. URL param names are the confirmed camelCase values, sent as
// literal "true" when ON; OFF means the param is simply absent (never "false").
const AVAILABILITY_IS_FBN_PARAM = "isFbn"; // -> backend is_fbn
const AVAILABILITY_RETURNS_PARAM = "returns"; // -> backend returns
const AVAILABILITY_HAS_TRACER_PARAM = "hasTracer"; // -> backend has_tracer

const availabilityIsFbnController = createBooleanToggleController({
  param: AVAILABILITY_IS_FBN_PARAM,
  elementId: "availabilityIsFbn",
});
const getAvailabilityIsFbnFromUi = availabilityIsFbnController.getFromUi;
const getAvailabilityIsFbnFromUrl = availabilityIsFbnController.getFromUrl;
const setAvailabilityIsFbnUi = availabilityIsFbnController.setUi;
const syncAvailabilityIsFbnUrl = availabilityIsFbnController.syncUrl;
const initAvailabilityIsFbnFromUrl = availabilityIsFbnController.initFromUrl;

const availabilityReturnsController = createBooleanToggleController({
  param: AVAILABILITY_RETURNS_PARAM,
  elementId: "availabilityReturns",
});
const getAvailabilityReturnsFromUi = availabilityReturnsController.getFromUi;
const getAvailabilityReturnsFromUrl = availabilityReturnsController.getFromUrl;
const setAvailabilityReturnsUi = availabilityReturnsController.setUi;
const syncAvailabilityReturnsUrl = availabilityReturnsController.syncUrl;
const initAvailabilityReturnsFromUrl = availabilityReturnsController.initFromUrl;

const availabilityHasTracerController = createBooleanToggleController({
  param: AVAILABILITY_HAS_TRACER_PARAM,
  elementId: "availabilityHasTracer",
});
const getAvailabilityHasTracerFromUi = availabilityHasTracerController.getFromUi;
const getAvailabilityHasTracerFromUrl = availabilityHasTracerController.getFromUrl;
const setAvailabilityHasTracerUi = availabilityHasTracerController.setUi;
const syncAvailabilityHasTracerUrl = availabilityHasTracerController.syncUrl;
const initAvailabilityHasTracerFromUrl = availabilityHasTracerController.initFromUrl;

/** Builds a single-select radio-group controller (Estimated-Delivery-style):
 * get/set/sync/init for one radio group <-> one URL param where at most one
 * of a fixed list of customer-facing values is active, or none (the default/
 * "Any" option). Mirrors createBooleanToggleController's shape (absent param
 * === default) and createMultiSelectController's normalize-against-an-
 * allowed-list approach, but for a single exclusive value instead of a list
 * or a boolean - so single-select filters plug into restoreFiltersFromUrl,
 * initListeners and collectActiveServerFilters the exact same way every
 * other control here does. */
function createSingleSelectController(config) {
  function normalizeValue(value) {
    if (value == null || value === "") return null;
    const normalized = String(value).trim();
    return config.values.includes(normalized) ? normalized : null;
  }

  function getFromUi() {
    const el = document.querySelector(
      `input[name="${config.radioName}"]:checked`
    );
    return el ? normalizeValue(el.value) : null;
  }

  function getFromUrl() {
    try {
      const params = new URLSearchParams(window.location.search);
      if (!params.has(config.param)) return { kind: "absent", value: null };
      const value = normalizeValue(params.get(config.param));
      if (!value) return { kind: "invalid", value: null };
      return { kind: "ok", value };
    } catch (err) {
      return { kind: "absent", value: null };
    }
  }

  function setUi(value) {
    const normalized = normalizeValue(value);
    document
      .querySelectorAll(`input[name="${config.radioName}"]`)
      .forEach((radio) => {
        radio.checked = normalized
          ? radio.value === normalized
          : radio.value === config.defaultValue;
      });
    return normalized;
  }

  // value falsy -> remove this control's own param (back to the default
  // option). Never touches other filters.
  function syncUrl(value, replace) {
    const normalized = normalizeValue(value);
    try {
      const url = new URL(window.location.href);
      const has = url.searchParams.has(config.param);
      if (!normalized) {
        if (!has) return;
        url.searchParams.delete(config.param);
      } else {
        if (has && url.searchParams.get(config.param) === normalized) return;
        url.searchParams.set(config.param, normalized);
      }
      const next = url.pathname + url.search + url.hash;
      if (replace) {
        history.replaceState(history.state, "", next);
      } else {
        history.pushState(history.state, "", next);
      }
    } catch (err) {
      // Ignore URL sync failures so search still works.
    }
  }

  function initFromUrl() {
    const raw = getFromUrl();
    if (raw.kind === "absent") {
      setUi(null);
      return null;
    }
    if (raw.kind === "invalid") {
      setUi(null);
      syncUrl(null, true);
      return null;
    }
    setUi(raw.value);
    syncUrl(raw.value, true);
    return raw.value;
  }

  return { getFromUi, getFromUrl, setUi, syncUrl, initFromUrl };
}

// Estimated Delivery (More Filters, Availability) — single-select radio:
// Any Date / Within 7 / 10 / 14 Business Days. The UI and URL only ever
// expose these customer-facing windows; Nivoda supplier-to-Ivanov delivery
// thresholds/windows (2/6/10) are internal and are mapped in
// AVAILABILITY_DELIVERY_TIME_TO_API_VALUE, used only right before the
// GraphQL request (see collectActiveServerFilters). Do NOT expose 2/6/10
// anywhere in markup, the URL, or a chip label.
const AVAILABILITY_DELIVERY_TIME_PARAM = "deliveryTime";
const AVAILABILITY_DELIVERY_TIME_VALUES = ["7", "10", "14"];
const AVAILABILITY_DELIVERY_TIME_RADIO_NAME = "availabilityDeliveryTime";
const AVAILABILITY_DELIVERY_TIME_DEFAULT_VALUE = "any";
const AVAILABILITY_DELIVERY_TIME_TO_API_VALUE = {
  "7": [2],
  "10": [6],
  "14": [10],
};

const availabilityDeliveryTimeController = createSingleSelectController({
  param: AVAILABILITY_DELIVERY_TIME_PARAM,
  values: AVAILABILITY_DELIVERY_TIME_VALUES,
  radioName: AVAILABILITY_DELIVERY_TIME_RADIO_NAME,
  defaultValue: AVAILABILITY_DELIVERY_TIME_DEFAULT_VALUE,
});
const getAvailabilityDeliveryTimeFromUi = availabilityDeliveryTimeController.getFromUi;
const getAvailabilityDeliveryTimeFromUrl = availabilityDeliveryTimeController.getFromUrl;
const setAvailabilityDeliveryTimeUi = availabilityDeliveryTimeController.setUi;
const syncAvailabilityDeliveryTimeUrl = availabilityDeliveryTimeController.syncUrl;
const initAvailabilityDeliveryTimeFromUrl = availabilityDeliveryTimeController.initFromUrl;

// Sort (#diamondSort) — single-select dropdown, same omit-default URL
// convention as Estimated Delivery. Featured / missing / invalid never
// reach the backend as `sort=`. Valid non-Featured values pass through
// as-is (price-asc / price-desc / carat-desc / carat-asc).
function normalizeSortValue(value) {
  if (value == null || value === "") return null;
  const normalized = String(value).trim().toLowerCase();
  if (!SORT_VALUES.includes(normalized) || normalized === SORT_DEFAULT) {
    return null;
  }
  return normalized;
}

function getSortFromUi() {
  const el = document.getElementById(SORT_ELEMENT_ID);
  return normalizeSortValue(el && el.value);
}

function getSortFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (!params.has(SORT_PARAM)) return { kind: "absent", value: null };
    const raw = String(params.get(SORT_PARAM)).trim().toLowerCase();
    if (raw === SORT_DEFAULT) return { kind: "default", value: null };
    const value = normalizeSortValue(raw);
    if (!value) return { kind: "invalid", value: null };
    return { kind: "ok", value };
  } catch (err) {
    return { kind: "absent", value: null };
  }
}

function setSortUi(value) {
  const el = document.getElementById(SORT_ELEMENT_ID);
  const normalized = normalizeSortValue(value);
  if (el) el.value = normalized || SORT_DEFAULT;
  return normalized;
}

function syncSortUrl(value, replace) {
  const normalized = normalizeSortValue(value);
  try {
    const url = new URL(window.location.href);
    const has = url.searchParams.has(SORT_PARAM);
    if (!normalized) {
      if (!has) return;
      url.searchParams.delete(SORT_PARAM);
    } else {
      if (has && url.searchParams.get(SORT_PARAM) === normalized) return;
      url.searchParams.set(SORT_PARAM, normalized);
    }
    const next = url.pathname + url.search + url.hash;
    if (replace) {
      history.replaceState(history.state, "", next);
    } else {
      history.pushState(history.state, "", next);
    }
  } catch (err) {
    // Ignore URL sync failures so search still works.
  }
}

function initSortFromUrl() {
  const raw = getSortFromUrl();
  if (raw.kind === "absent") {
    setSortUi(null);
    return null;
  }
  if (raw.kind === "invalid" || raw.kind === "default") {
    setSortUi(null);
    syncSortUrl(null, true);
    return null;
  }
  setSortUi(raw.value);
  syncSortUrl(raw.value, true);
  return raw.value;
}

function nearlyEqualCaratBound(a, b) {
  return Math.abs(a - b) < 0.001;
}

function roundCaratBound(n) {
  return Math.round(n * 100) / 100;
}

function clampCaratBound(n) {
  if (!Number.isFinite(n)) return CARAT_DEFAULT_MIN;
  return roundCaratBound(
    Math.min(CARAT_DEFAULT_MAX, Math.max(CARAT_DEFAULT_MIN, n))
  );
}

function formatCaratUrlValue(n) {
  const rounded = roundCaratBound(n);
  if (Number.isInteger(rounded)) return String(rounded);
  return String(rounded);
}

function isDefaultCaratRange(min, max) {
  return (
    nearlyEqualCaratBound(min, CARAT_DEFAULT_MIN) &&
    nearlyEqualCaratBound(max, CARAT_DEFAULT_MAX)
  );
}

function normalizeCaratRange(min, max) {
  let nextMin = clampCaratBound(min);
  let nextMax = clampCaratBound(max);
  if (nextMin > nextMax) {
    const swap = nextMin;
    nextMin = nextMax;
    nextMax = swap;
  }
  return { min: nextMin, max: nextMax };
}

// Returns { kind: "absent"|"invalid"|"ok", min?, max? }.
function readCaratParamsFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const hasMin = params.has(CARAT_MIN_PARAM);
    const hasMax = params.has(CARAT_MAX_PARAM);
    if (!hasMin && !hasMax) return { kind: "absent" };
    // One without the other is invalid.
    if (!hasMin || !hasMax) return { kind: "invalid" };
    const min = parseFloat(params.get(CARAT_MIN_PARAM));
    const max = parseFloat(params.get(CARAT_MAX_PARAM));
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      return { kind: "invalid" };
    }
    return { kind: "ok", min, max };
  } catch (err) {
    return { kind: "absent" };
  }
}

// min/max null → clear both params (default / no Carat filter).
// Otherwise set both; never touches origin/shape/other filters.
function syncCaratUrl(min, max, replace) {
  try {
    const url = new URL(window.location.href);
    const clear =
      min == null ||
      max == null ||
      !Number.isFinite(min) ||
      !Number.isFinite(max) ||
      isDefaultCaratRange(min, max);

    if (clear) {
      if (
        !url.searchParams.has(CARAT_MIN_PARAM) &&
        !url.searchParams.has(CARAT_MAX_PARAM)
      ) {
        return;
      }
      url.searchParams.delete(CARAT_MIN_PARAM);
      url.searchParams.delete(CARAT_MAX_PARAM);
    } else {
      const range = normalizeCaratRange(min, max);
      const nextMin = formatCaratUrlValue(range.min);
      const nextMax = formatCaratUrlValue(range.max);
      if (
        url.searchParams.get(CARAT_MIN_PARAM) === nextMin &&
        url.searchParams.get(CARAT_MAX_PARAM) === nextMax
      ) {
        return;
      }
      url.searchParams.set(CARAT_MIN_PARAM, nextMin);
      url.searchParams.set(CARAT_MAX_PARAM, nextMax);
    }

    const next = url.pathname + url.search + url.hash;
    if (replace) {
      history.replaceState(history.state, "", next);
    } else {
      history.pushState(history.state, "", next);
    }
  } catch (err) {
    // Ignore URL sync failures so search still works.
  }
}

// Restore slider / popular / Min / Max / chip via the liquid bridge when present.
function setCaratUi(min, max) {
  if (typeof window.setCaratUiFromUrl === "function") {
    window.setCaratUiFromUrl(min, max);
    return;
  }
  const minInput = document.getElementById("caratMin");
  const maxInput = document.getElementById("caratMax");
  if (!minInput || !maxInput) return;
  if (min == null || max == null) {
    minInput.value = formatCaratUrlValue(CARAT_DEFAULT_MIN);
    maxInput.value = CARAT_DEFAULT_MAX.toFixed(2);
    return;
  }
  const range = normalizeCaratRange(min, max);
  minInput.value = range.min.toFixed(2);
  maxInput.value = range.max.toFixed(2);
}

function initCaratFromUrl() {
  const raw = readCaratParamsFromUrl();
  if (raw.kind === "absent") {
    setCaratUi(null, null);
    return null;
  }
  if (raw.kind === "invalid") {
    // Strip invalid carat params without creating a history entry.
    setCaratUi(null, null);
    syncCaratUrl(null, null, true);
    return null;
  }

  const range = normalizeCaratRange(raw.min, raw.max);
  if (isDefaultCaratRange(range.min, range.max)) {
    setCaratUi(null, null);
    syncCaratUrl(null, null, true);
    return null;
  }

  setCaratUi(range.min, range.max);
  // Normalize clamped/formatted values in the URL (replaceState).
  syncCaratUrl(range.min, range.max, true);
  return range;
}

// Coalesce rapid slider/typing updates: first change in a burst pushes a
// history entry; follow-ups replace that entry so Back still works.
let caratUrlSyncReplace = false;
let caratUrlSyncResetTimer = null;
function syncCaratUrlFromUi(min, max) {
  syncCaratUrl(min, max, caratUrlSyncReplace);
  caratUrlSyncReplace = true;
  if (caratUrlSyncResetTimer) clearTimeout(caratUrlSyncResetTimer);
  caratUrlSyncResetTimer = setTimeout(() => {
    caratUrlSyncReplace = false;
    caratUrlSyncResetTimer = null;
  }, 400);
}

function roundPriceBound(n) {
  return Math.round(n);
}

function clampPriceBound(n) {
  if (!Number.isFinite(n)) return PRICE_BOUND_MIN;
  return roundPriceBound(
    Math.min(PRICE_BOUND_MAX, Math.max(PRICE_BOUND_MIN, n))
  );
}

function formatPriceUrlValue(n) {
  return String(roundPriceBound(n));
}

function normalizePriceRange(min, max) {
  let nextMin = clampPriceBound(min);
  let nextMax = clampPriceBound(max);
  if (nextMin > nextMax) {
    const swap = nextMin;
    nextMin = nextMax;
    nextMax = swap;
  }
  return { min: nextMin, max: nextMax };
}

// Returns { kind: "absent"|"invalid"|"ok", min?, max? }.
function readPriceParamsFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const hasMin = params.has(PRICE_MIN_PARAM);
    const hasMax = params.has(PRICE_MAX_PARAM);
    if (!hasMin && !hasMax) return { kind: "absent" };
    // One without the other is invalid.
    if (!hasMin || !hasMax) return { kind: "invalid" };
    const min = parseFloat(params.get(PRICE_MIN_PARAM));
    const max = parseFloat(params.get(PRICE_MAX_PARAM));
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      return { kind: "invalid" };
    }
    return { kind: "ok", min, max };
  } catch (err) {
    return { kind: "absent" };
  }
}

// min/max null → clear both params (empty inputs / no Price filter).
// Otherwise set both; never touches origin/shape/carat/other filters.
function syncPriceUrl(min, max, replace) {
  try {
    const url = new URL(window.location.href);
    const clear =
      min == null ||
      max == null ||
      !Number.isFinite(min) ||
      !Number.isFinite(max);

    if (clear) {
      if (
        !url.searchParams.has(PRICE_MIN_PARAM) &&
        !url.searchParams.has(PRICE_MAX_PARAM)
      ) {
        return;
      }
      url.searchParams.delete(PRICE_MIN_PARAM);
      url.searchParams.delete(PRICE_MAX_PARAM);
    } else {
      const range = normalizePriceRange(min, max);
      const nextMin = formatPriceUrlValue(range.min);
      const nextMax = formatPriceUrlValue(range.max);
      if (
        url.searchParams.get(PRICE_MIN_PARAM) === nextMin &&
        url.searchParams.get(PRICE_MAX_PARAM) === nextMax
      ) {
        return;
      }
      url.searchParams.set(PRICE_MIN_PARAM, nextMin);
      url.searchParams.set(PRICE_MAX_PARAM, nextMax);
    }

    const next = url.pathname + url.search + url.hash;
    if (replace) {
      history.replaceState(history.state, "", next);
    } else {
      history.pushState(history.state, "", next);
    }
  } catch (err) {
    // Ignore URL sync failures so search still works.
  }
}

// Restore Min / Max / chip / future slider via the liquid bridge when present.
function setPriceUi(min, max) {
  if (typeof window.setPriceUiFromUrl === "function") {
    window.setPriceUiFromUrl(min, max);
    return;
  }
  const minInput = document.getElementById("priceMin");
  const maxInput = document.getElementById("priceMax");
  if (!minInput || !maxInput) return;
  if (min == null || max == null) {
    minInput.value = "";
    maxInput.value = "";
    return;
  }
  const range = normalizePriceRange(min, max);
  minInput.value = formatPriceUrlValue(range.min);
  maxInput.value = formatPriceUrlValue(range.max);
}

function initPriceFromUrl() {
  const raw = readPriceParamsFromUrl();
  if (raw.kind === "absent") {
    setPriceUi(null, null);
    return null;
  }
  if (raw.kind === "invalid") {
    // Strip invalid price params without creating a history entry.
    setPriceUi(null, null);
    syncPriceUrl(null, null, true);
    return null;
  }

  const range = normalizePriceRange(raw.min, raw.max);
  setPriceUi(range.min, range.max);
  // Normalize clamped/formatted values in the URL (replaceState).
  syncPriceUrl(range.min, range.max, true);
  return range;
}

// Coalesce rapid typing updates: first change in a burst pushes a
// history entry; follow-ups replace that entry so Back still works.
let priceUrlSyncReplace = false;
let priceUrlSyncResetTimer = null;
function syncPriceUrlFromUi(min, max) {
  syncPriceUrl(min, max, priceUrlSyncReplace);
  priceUrlSyncReplace = true;
  if (priceUrlSyncResetTimer) clearTimeout(priceUrlSyncResetTimer);
  priceUrlSyncResetTimer = setTimeout(() => {
    priceUrlSyncReplace = false;
    priceUrlSyncResetTimer = null;
  }, 400);
}

/** Builds the shared linear dual-range controller (Carat/Price-style: full
 * bound range = no filter) used by all seven Geometry numeric filters
 * instead of seven hand-duplicated blocks. `config` is one entry of
 * GEOMETRY_RANGE_CONFIGS above. Restoring the UI defers to
 * window.set<Bridge>UiFromUrl (defined in sections/diamond-search.liquid's
 * createGeometryRangeUi), exactly like setCaratUi/setPriceUi/etc. above. */
function createLinearRangeController(config) {
  function round(n) {
    const m = Math.pow(10, config.decimals);
    return Math.round(n * m) / m;
  }

  function clampBound(n) {
    if (!Number.isFinite(n)) return config.boundMin;
    return round(Math.min(config.boundMax, Math.max(config.boundMin, n)));
  }

  function formatUrlValue(n) {
    return clampBound(n).toFixed(config.decimals);
  }

  function isDefaultRange(min, max) {
    const epsilon = Math.pow(10, -config.decimals) / 2 + 1e-9;
    return (
      Math.abs(min - config.boundMin) < epsilon &&
      Math.abs(max - config.boundMax) < epsilon
    );
  }

  function normalizeRange(min, max) {
    let nextMin = clampBound(min);
    let nextMax = clampBound(max);
    if (nextMin > nextMax) {
      const swap = nextMin;
      nextMin = nextMax;
      nextMax = swap;
    }
    return { min: nextMin, max: nextMax };
  }

  function getRangeFromUi() {
    const minEl = document.getElementById(config.key + "Min");
    const maxEl = document.getElementById(config.key + "Max");
    const min = minEl ? parseFloat(minEl.value) : NaN;
    const max = maxEl ? parseFloat(maxEl.value) : NaN;
    if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
    return { min, max };
  }

  // Returns { kind: "absent"|"invalid"|"ok", min?, max? }.
  function readParamsFromUrl() {
    try {
      const params = new URLSearchParams(window.location.search);
      const hasMin = params.has(config.minParam);
      const hasMax = params.has(config.maxParam);
      if (!hasMin && !hasMax) return { kind: "absent" };
      if (!hasMin || !hasMax) return { kind: "invalid" };
      const min = parseFloat(params.get(config.minParam));
      const max = parseFloat(params.get(config.maxParam));
      if (!Number.isFinite(min) || !Number.isFinite(max)) {
        return { kind: "invalid" };
      }
      return { kind: "ok", min, max };
    } catch (err) {
      return { kind: "absent" };
    }
  }

  // min/max null → clear both params (default / no filter). Otherwise set
  // both together; never touches other filters (mirrors syncCaratUrl).
  function syncUrl(min, max, replace) {
    try {
      const url = new URL(window.location.href);
      const clear =
        min == null ||
        max == null ||
        !Number.isFinite(min) ||
        !Number.isFinite(max) ||
        isDefaultRange(min, max);

      if (clear) {
        if (
          !url.searchParams.has(config.minParam) &&
          !url.searchParams.has(config.maxParam)
        ) {
          return;
        }
        url.searchParams.delete(config.minParam);
        url.searchParams.delete(config.maxParam);
      } else {
        const range = normalizeRange(min, max);
        const nextMin = formatUrlValue(range.min);
        const nextMax = formatUrlValue(range.max);
        if (
          url.searchParams.get(config.minParam) === nextMin &&
          url.searchParams.get(config.maxParam) === nextMax
        ) {
          return;
        }
        url.searchParams.set(config.minParam, nextMin);
        url.searchParams.set(config.maxParam, nextMax);
      }

      const next = url.pathname + url.search + url.hash;
      if (replace) {
        history.replaceState(history.state, "", next);
      } else {
        history.pushState(history.state, "", next);
      }
    } catch (err) {
      // Ignore URL sync failures so search still works.
    }
  }

  // Restore slider / Min / Max / chip via the liquid bridge when present.
  function setUi(min, max) {
    const bridgeFn = window["set" + config.bridge + "UiFromUrl"];
    if (typeof bridgeFn === "function") {
      bridgeFn(min, max);
      return;
    }
    const minEl = document.getElementById(config.key + "Min");
    const maxEl = document.getElementById(config.key + "Max");
    if (!minEl || !maxEl) return;
    if (min == null || max == null) {
      minEl.value = formatUrlValue(config.boundMin);
      maxEl.value = formatUrlValue(config.boundMax);
      return;
    }
    const range = normalizeRange(min, max);
    minEl.value = formatUrlValue(range.min);
    maxEl.value = formatUrlValue(range.max);
  }

  function initFromUrl() {
    const raw = readParamsFromUrl();
    if (raw.kind === "absent") {
      setUi(null, null);
      return null;
    }
    if (raw.kind === "invalid") {
      setUi(null, null);
      syncUrl(null, null, true);
      return null;
    }

    const range = normalizeRange(raw.min, raw.max);
    if (isDefaultRange(range.min, range.max)) {
      setUi(null, null);
      syncUrl(null, null, true);
      return null;
    }

    setUi(range.min, range.max);
    // Normalize clamped/formatted values in the URL (replaceState).
    syncUrl(range.min, range.max, true);
    return range;
  }

  // Coalesce rapid slider/typing updates: first change in a burst pushes a
  // history entry; follow-ups replace that entry so Back still works.
  let urlSyncReplace = false;
  let urlSyncResetTimer = null;
  function syncUrlFromUi(min, max) {
    syncUrl(min, max, urlSyncReplace);
    urlSyncReplace = true;
    if (urlSyncResetTimer) clearTimeout(urlSyncResetTimer);
    urlSyncResetTimer = setTimeout(() => {
      urlSyncReplace = false;
      urlSyncResetTimer = null;
    }, 400);
  }

  // Coalesce dual Min+Max "input" events into one request; reloadRequestId
  // still drops any stale responses (mirrors scheduleCaratReload).
  let reloadScheduled = false;
  function scheduleReload() {
    if (reloadScheduled) return;
    reloadScheduled = true;
    setTimeout(() => {
      reloadScheduled = false;
      const range = getRangeFromUi();
      if (!range) return;
      syncUrlFromUi(range.min, range.max);
      reloadDiamonds(collectActiveServerFilters());
    }, 0);
  }

  return {
    config,
    isDefaultRange,
    normalizeRange,
    getRangeFromUi,
    readParamsFromUrl,
    setUi,
    syncUrl,
    initFromUrl,
    scheduleReload,
  };
}

const geometryRangeControllers = {};
GEOMETRY_RANGE_CONFIGS.forEach((cfg) => {
  geometryRangeControllers[cfg.key] = createLinearRangeController(cfg);
});

function restoreFiltersFromUrl(options) {
  options = options || {};
  if (typeof window.cancelPendingCaratSearch === "function") {
    window.cancelPendingCaratSearch();
  }
  cancelPendingCaratReload();

  const origin = getOriginFromUrl();
  setOriginUi(origin);

  const rawShape = getRawShapeParamFromUrl();
  if (rawShape == null || rawShape === "") {
    setShapeUi(null);
  } else {
    const shape = shapeSlugToLabel(rawShape);
    if (!shape) {
      setShapeUi(null);
      syncShapeUrl(null, true);
    } else {
      setShapeUi(shape);
      syncShapeUrl(shape, true);
    }
  }

  const rawColor = readColorParamsFromUrl();
  if (rawColor.kind === "absent") {
    setColorUi(null, null);
  } else if (rawColor.kind === "invalid") {
    setColorUi(null, null);
    syncColorUrl(null, null, true);
  } else {
    const range = normalizeColorRange(rawColor.min, rawColor.max);
    if (isDefaultColorRange(range.min, range.max)) {
      setColorUi(null, null);
      syncColorUrl(null, null, true);
    } else {
      setColorUi(range.min, range.max);
      syncColorUrl(range.min, range.max, true);
    }
  }

  const rawClarity = readClarityParamsFromUrl();
  if (rawClarity.kind === "absent") {
    setClarityUi(null, null);
  } else if (rawClarity.kind === "invalid") {
    setClarityUi(null, null);
    syncClarityUrl(null, null, true);
  } else {
    const range = normalizeClarityRange(rawClarity.min, rawClarity.max);
    if (isDefaultClarityRange(range.min, range.max)) {
      setClarityUi(null, null);
      syncClarityUrl(null, null, true);
    } else {
      setClarityUi(range.min, range.max);
      syncClarityUrl(range.min, range.max, true);
    }
  }

  const rawCut = readCutParamsFromUrl();
  if (rawCut.kind === "absent") {
    setCutUi(null, null);
  } else if (rawCut.kind === "invalid") {
    setCutUi(null, null);
    syncCutUrl(null, null, true);
  } else {
    const range = normalizeCutRange(rawCut.min, rawCut.max);
    if (isDefaultCutRange(range.min, range.max)) {
      setCutUi(null, null);
      syncCutUrl(null, null, true);
    } else {
      setCutUi(range.min, range.max);
      syncCutUrl(range.min, range.max, true);
    }
  }

  const rawPolish = readPolishParamsFromUrl();
  if (rawPolish.kind === "absent") {
    setPolishUi(null, null);
  } else if (rawPolish.kind === "invalid") {
    setPolishUi(null, null);
    syncPolishUrl(null, null, true);
  } else {
    const range = normalizePolishRange(rawPolish.min, rawPolish.max);
    if (isDefaultPolishRange(range.min, range.max)) {
      setPolishUi(null, null);
      syncPolishUrl(null, null, true);
    } else {
      setPolishUi(range.min, range.max);
      syncPolishUrl(range.min, range.max, true);
    }
  }

  const rawSymmetry = readSymmetryParamsFromUrl();
  if (rawSymmetry.kind === "absent") {
    setSymmetryUi(null, null);
  } else if (rawSymmetry.kind === "invalid") {
    setSymmetryUi(null, null);
    syncSymmetryUrl(null, null, true);
  } else {
    const range = normalizeSymmetryRange(rawSymmetry.min, rawSymmetry.max);
    if (isDefaultSymmetryRange(range.min, range.max)) {
      setSymmetryUi(null, null);
      syncSymmetryUrl(null, null, true);
    } else {
      setSymmetryUi(range.min, range.max);
      syncSymmetryUrl(range.min, range.max, true);
    }
  }

  const rawFluorescence = readFluorescenceParamsFromUrl();
  if (rawFluorescence.kind === "absent") {
    setFluorescenceUi(null, null);
  } else if (rawFluorescence.kind === "invalid") {
    setFluorescenceUi(null, null);
    syncFluorescenceUrl(null, null, true);
  } else {
    const range = normalizeFluorescenceRange(rawFluorescence.min, rawFluorescence.max);
    if (isDefaultFluorescenceRange(range.min, range.max)) {
      setFluorescenceUi(null, null);
      syncFluorescenceUrl(null, null, true);
    } else {
      setFluorescenceUi(range.min, range.max);
      syncFluorescenceUrl(range.min, range.max, true);
    }
  }

  const rawCertificateLab = getCertificateLabsFromUrl();
  if (rawCertificateLab.kind === "absent") {
    setCertificateLabUi([]);
  } else if (rawCertificateLab.kind === "invalid") {
    setCertificateLabUi([]);
    syncCertificateLabUrl([], true);
  } else {
    setCertificateLabUi(rawCertificateLab.labs);
    syncCertificateLabUrl(rawCertificateLab.labs, true);
  }

  GEOMETRY_RANGE_CONFIGS.forEach((cfg) => {
    const controller = geometryRangeControllers[cfg.key];
    const raw = controller.readParamsFromUrl();
    if (raw.kind === "absent") {
      controller.setUi(null, null);
      return;
    }
    if (raw.kind === "invalid") {
      controller.setUi(null, null);
      controller.syncUrl(null, null, true);
      return;
    }
    const range = controller.normalizeRange(raw.min, raw.max);
    if (controller.isDefaultRange(range.min, range.max)) {
      controller.setUi(null, null);
      controller.syncUrl(null, null, true);
    } else {
      controller.setUi(range.min, range.max);
      controller.syncUrl(range.min, range.max, true);
    }
  });

  const rawGirdle = getGirdleFromUrl();
  if (rawGirdle.kind === "absent") {
    setGirdleUi([]);
  } else if (rawGirdle.kind === "invalid") {
    setGirdleUi([]);
    syncGirdleUrl([], true);
  } else {
    setGirdleUi(rawGirdle.labels);
    syncGirdleUrl(rawGirdle.labels, true);
  }

  const rawCulet = getCuletFromUrl();
  if (rawCulet.kind === "absent") {
    setCuletUi([]);
  } else if (rawCulet.kind === "invalid") {
    setCuletUi([]);
    syncCuletUrl([], true);
  } else {
    setCuletUi(rawCulet.labels);
    syncCuletUrl(rawCulet.labels, true);
  }

  // Media (Images / 360° / Certificate PDF) — each toggle restores fully
  // independently of the other two; clearing/normalizing one never touches
  // the others' URL params. Images uses the same default-ON init path as
  // first load so popstate / Reset All cannot drift from initFromUrl.
  initMediaHasImageFromUrl();

  const rawMediaHasV360 = getMediaHasV360FromUrl();
  if (rawMediaHasV360.kind === "absent") {
    setMediaHasV360Ui(false);
  } else if (rawMediaHasV360.kind === "invalid") {
    setMediaHasV360Ui(false);
    syncMediaHasV360Url(false, true);
  } else {
    setMediaHasV360Ui(true);
    syncMediaHasV360Url(true, true);
  }

  const rawMediaHasCertPdf = getMediaHasCertPdfFromUrl();
  if (rawMediaHasCertPdf.kind === "absent") {
    setMediaHasCertPdfUi(false);
  } else if (rawMediaHasCertPdf.kind === "invalid") {
    setMediaHasCertPdfUi(false);
    syncMediaHasCertPdfUrl(false, true);
  } else {
    setMediaHasCertPdfUi(true);
    syncMediaHasCertPdfUrl(true, true);
  }

  // Availability (Faster Fulfillment / Returnable / Traced) — each toggle
  // restores fully independently of the others, same as Media's three toggles.
  const rawAvailabilityIsFbn = getAvailabilityIsFbnFromUrl();
  if (rawAvailabilityIsFbn.kind === "absent") {
    setAvailabilityIsFbnUi(false);
  } else if (rawAvailabilityIsFbn.kind === "invalid") {
    setAvailabilityIsFbnUi(false);
    syncAvailabilityIsFbnUrl(false, true);
  } else {
    setAvailabilityIsFbnUi(true);
    syncAvailabilityIsFbnUrl(true, true);
  }

  const rawAvailabilityReturns = getAvailabilityReturnsFromUrl();
  if (rawAvailabilityReturns.kind === "absent") {
    setAvailabilityReturnsUi(false);
  } else if (rawAvailabilityReturns.kind === "invalid") {
    setAvailabilityReturnsUi(false);
    syncAvailabilityReturnsUrl(false, true);
  } else {
    setAvailabilityReturnsUi(true);
    syncAvailabilityReturnsUrl(true, true);
  }

  const rawAvailabilityHasTracer = getAvailabilityHasTracerFromUrl();
  if (rawAvailabilityHasTracer.kind === "absent") {
    setAvailabilityHasTracerUi(false);
  } else if (rawAvailabilityHasTracer.kind === "invalid") {
    setAvailabilityHasTracerUi(false);
    syncAvailabilityHasTracerUrl(false, true);
  } else {
    setAvailabilityHasTracerUi(true);
    syncAvailabilityHasTracerUrl(true, true);
  }

  // Estimated Delivery (single-select radio) — "Any Date" is the default
  // (absent param); 7/10/14 restore to their own radio option.
  const rawAvailabilityDeliveryTime = getAvailabilityDeliveryTimeFromUrl();
  if (rawAvailabilityDeliveryTime.kind === "absent") {
    setAvailabilityDeliveryTimeUi(null);
  } else if (rawAvailabilityDeliveryTime.kind === "invalid") {
    setAvailabilityDeliveryTimeUi(null);
    syncAvailabilityDeliveryTimeUrl(null, true);
  } else {
    setAvailabilityDeliveryTimeUi(rawAvailabilityDeliveryTime.value);
    syncAvailabilityDeliveryTimeUrl(rawAvailabilityDeliveryTime.value, true);
  }

  const rawCarat = readCaratParamsFromUrl();
  if (rawCarat.kind === "absent") {
    setCaratUi(null, null);
  } else if (rawCarat.kind === "invalid") {
    setCaratUi(null, null);
    syncCaratUrl(null, null, true);
  } else {
    const range = normalizeCaratRange(rawCarat.min, rawCarat.max);
    if (isDefaultCaratRange(range.min, range.max)) {
      setCaratUi(null, null);
      syncCaratUrl(null, null, true);
    } else {
      setCaratUi(range.min, range.max);
      syncCaratUrl(range.min, range.max, true);
    }
  }

  const rawPrice = readPriceParamsFromUrl();
  if (rawPrice.kind === "absent") {
    setPriceUi(null, null);
  } else if (rawPrice.kind === "invalid") {
    setPriceUi(null, null);
    syncPriceUrl(null, null, true);
  } else {
    const range = normalizePriceRange(rawPrice.min, rawPrice.max);
    setPriceUi(range.min, range.max);
    syncPriceUrl(range.min, range.max, true);
  }

  // Sort (toolbar) — Featured / missing / invalid omit the param;
  // valid non-Featured values restore the dropdown and stay in the URL.
  initSortFromUrl();

  document.dispatchEvent(new CustomEvent("diamond:origin-ui-ready"));
  if (options.skipReload) return;
  reloadDiamonds(collectActiveServerFilters());
}

function initUrlNavigation() {
  window.addEventListener("popstate", () => {
    const compare = window.DiamondSearchCompare;
    if (compare && typeof compare.onPopState === "function" && compare.onPopState()) {
      return;
    }
    restoreFiltersFromUrl();
  });
}

function getDiamondGrid() {
  return document.getElementById("diamondGrid");
}

function getDiamondSearchMain() {
  const grid = getDiamondGrid();
  return grid && grid.closest(".diamond-search__main");
}

function getDiamondSearchToolbar() {
  const main = getDiamondSearchMain();
  return main && main.querySelector(".diamond-search__toolbar");
}

function getDiamondListHeader() {
  const main = getDiamondSearchMain();
  return main && main.querySelector(".diamond-list-header");
}

// Full-reload only: keep #diamondGrid from collapsing to 6 skeletons
// (document height shrink → browser scroll clamp → visible jump).
// Skip when there is no previous results grid (initial 6-skeleton
// first paint). Load More never calls these helpers.
function preserveDiamondGridHeight(grid) {
  if (!grid) return;
  if (grid.style.minHeight) return;
  if (!grid.querySelector(".diamond-card")) return;
  const height = grid.offsetHeight;
  if (!(height > 0)) return;
  grid.style.minHeight = height + "px";
}

function releaseDiamondGridHeight(grid) {
  if (!grid) return;
  grid.style.removeProperty("min-height");
}

function hasPreviousDiamondResults() {
  const grid = getDiamondGrid();
  return !!(grid && grid.querySelector(".diamond-card"));
}

function lockMinHeight(el) {
  if (!el || el.style.minHeight) return;
  const height = el.offsetHeight;
  if (!(height > 0)) return;
  el.style.minHeight = height + "px";
}

function unlockMinHeight(el) {
  if (!el) return;
  el.style.removeProperty("min-height");
}

// Full-reload only: the grid lock does not cover sibling chrome.
// reloadDiamonds() hides #diamondLoadMore (~76px), the chips row can
// collapse (~31px + 12px toolbar gap = 43px), and List header can
// hide — any of those reduce document height or shift the grid.
// Lock toolbar + results column at their pre-reload heights, and
// reserve the list-header slot, before those changes. Skip first
// paint (no previous cards). Load More append never calls these.
function preserveDiamondResultsColumnHeight() {
  const main = getDiamondSearchMain();
  const toolbar = getDiamondSearchToolbar();
  const alreadyLocked = !!(
    (main && main.style.minHeight) ||
    (toolbar && toolbar.style.minHeight)
  );
  if (!alreadyLocked && !hasPreviousDiamondResults()) return;

  if (main && !main.style.overflowAnchor) {
    // Header / Load More / chips can leave the flow during reload.
    // Disable overflow anchoring so the browser does not rewrite
    // window.scrollY while the reserved min-heights hold layout.
    main.style.overflowAnchor = "none";
  }
  lockMinHeight(toolbar);
  lockMinHeight(main);
  preserveDiamondListHeaderSlot();
}

function preserveDiamondListHeaderSlot() {
  const main = getDiamondSearchMain();
  const header = getDiamondListHeader();
  if (!main || !header || header.hidden) return;
  if (main.querySelector("[data-diamond-header-slot]")) return;
  const height = header.offsetHeight;
  if (!(height > 0)) return;
  let block = height;
  if (typeof window !== "undefined" && window.getComputedStyle) {
    const cs = window.getComputedStyle(header);
    block += (parseFloat(cs.marginTop) || 0) + (parseFloat(cs.marginBottom) || 0);
  }
  const slot = document.createElement("div");
  slot.setAttribute("data-diamond-header-slot", "true");
  slot.setAttribute("aria-hidden", "true");
  slot.style.height = block + "px";
  header.after(slot);
}

function releaseDiamondResultsColumnHeight() {
  const main = getDiamondSearchMain();
  unlockMinHeight(getDiamondSearchToolbar());
  unlockMinHeight(main);
  if (main) {
    main.style.removeProperty("overflow-anchor");
    const slot = main.querySelector("[data-diamond-header-slot]");
    if (slot && slot.parentNode) slot.parentNode.removeChild(slot);
  }
}

// Results paint restores Load More / list header via observers and
// syncLoadMoreAfterResults(). Release the column lock only after
// those updates so we do not uncover a second clamp. Stale reloads
// must not clear a newer lock.
function scheduleReleaseDiamondResultsColumnHeight() {
  const requestId = reloadRequestId;
  queueMicrotask(() => {
    if (requestId !== reloadRequestId) return;
    releaseDiamondResultsColumnHeight();
  });
}

function bindDiamondResultsRetry() {
  if (diamondResultsRetryBound) return;
  const grid = getDiamondGrid();
  if (!grid) return;
  diamondResultsRetryBound = true;
  grid.addEventListener("click", (e) => {
    const retry = e.target && e.target.closest && e.target.closest("[data-diamond-retry]");
    if (!retry) return;
    e.preventDefault();
    reloadDiamonds(collectActiveServerFilters());
  });
}

function renderDiamondResultsLoading(grid) {
  let html = "";
  for (let i = 0; i < DIAMOND_SKELETON_COUNT; i += 1) {
    html += '<div class="diamond-skeleton" aria-hidden="true"></div>';
  }
  grid.innerHTML = html;
}

function renderDiamondResultsEmpty(grid) {
  grid.innerHTML =
    '<div class="diamond-empty-state" role="status">' +
      '<p class="diamond-empty-state__title">No diamonds match your filters.</p>' +
      '<p class="diamond-empty-state__hint">Try adjusting or clearing one or more filters.</p>' +
    "</div>";
}

function renderDiamondResultsError(grid) {
  grid.innerHTML =
    '<div class="diamond-empty-state diamond-empty-state--error" role="status">' +
      '<p class="diamond-empty-state__title">We couldn\'t load diamonds right now.</p>' +
      '<p class="diamond-empty-state__hint">Please try again.</p>' +
      '<button type="button" class="diamond-results-retry" data-diamond-retry>Try Again</button>' +
    "</div>";
}

// Canonical results-area state. Callers must not independently swap
// #diamondGrid markup or the toolbar will treat a failed request as
// "0 diamonds". States:
//   loading — request in flight; skeletons replace any previous cards.
//             A previous results-grid height is locked first so the
//             document does not collapse under the 6-skeleton markup.
//   results — successful response with cards (renderLiveDiamondResults)
//   empty   — successful response, zero matching diamonds
//   error   — request failed; never use the empty-filter copy
function setDiamondResultsState(state) {
  const grid = getDiamondGrid();
  if (!grid) return;

  const next =
    state === "loading" || state === "results" || state === "empty" || state === "error"
      ? state
      : "error";

  grid.setAttribute("data-results-state", next);

  if (next === "loading") {
    preserveDiamondGridHeight(grid);
    grid.setAttribute("aria-busy", "true");
    renderDiamondResultsLoading(grid);
    return;
  }

  grid.setAttribute("aria-busy", "false");

  if (next === "empty") {
    renderDiamondResultsEmpty(grid);
    releaseDiamondGridHeight(grid);
    releaseDiamondResultsColumnHeight();
    return;
  }

  if (next === "error") {
    renderDiamondResultsError(grid);
    releaseDiamondGridHeight(grid);
    releaseDiamondResultsColumnHeight();
  }
}

// Single reusable request function used by initial load, Shape, Color,
// Clarity, Cut Grade, and Diamond Origin.
// Calls window.IvanovAPI.searchDiamonds(filters), extracts
// response.diamonds_by_query.items, maps them through mapDiamondItem,
// replaces allDiamonds, then calls applyFilters().
// Always sends labgrown from the active Origin UI so Shape/Color/etc.
// cannot drop the origin filter.
//
// Subsequent-request behavior: loading replaces any currently rendered
// cards immediately so shoppers never read a previous page as matching
// the newly selected filters. Failure shows the error state, not stale
// cards. Overlapping requests keep the existing reloadRequestId gate;
// an older response cannot overwrite a newer loading/results/error state.
let compareResultCountOverride = null;
let compareHoldsSearchGrid = false;
let searchFetchInFlight = false;

function getResultCount() {
  if (compareResultCountOverride != null) return compareResultCountOverride;
  return resultCount;
}

function readDiamondSearchMeta(response, fallbackOffset) {
  const block = response && response.diamonds_by_query;
  const rawCount = block && block.result_count;
  let count = null;
  if (typeof rawCount === "number" && Number.isFinite(rawCount)) {
    count = rawCount;
  } else if (typeof rawCount === "string" && rawCount.trim() !== "") {
    const parsed = Number(rawCount);
    if (Number.isFinite(parsed)) count = parsed;
  }

  const rawOffset = block && block.offset;
  const offset =
    typeof rawOffset === "number" && Number.isFinite(rawOffset)
      ? rawOffset
      : fallbackOffset;

  const rawItems = block && block.items;
  const itemCount = Array.isArray(rawItems) ? rawItems.length : 0;

  return {
    resultCount: count,
    hasMore: block && block.has_more === true,
    nextOffset: offset + itemCount,
  };
}

function diamondRecordId(d) {
  if (!d || d.id == null || d.id === "") return "";
  return String(d.id);
}

function isDiamondFavoriteEventTarget(target) {
  if (window.DiamondSaved && typeof window.DiamondSaved.isHeartTarget === "function") {
    return window.DiamondSaved.isHeartTarget(target);
  }
  if (!target) return false;
  if (typeof target.closest === "function") {
    return Boolean(target.closest(".diamond-card__favorite"));
  }
  return false;
}

function isDiamondDetailCtaEventTarget(target) {
  if (!target) return false;
  if (typeof target.closest === "function") {
    return Boolean(target.closest(".diamond-card__cta"));
  }
  return false;
}

function bindDiamondCardFavorite(favoriteBtn, d) {
  if (!favoriteBtn) return;
  const recordId = diamondRecordId(d);
  const Saved = window.DiamondSaved;

  if (Saved && typeof Saved.applyHeartState === "function") {
    Saved.applyHeartState(favoriteBtn, Saved.has(recordId));
  }

  favoriteBtn.addEventListener("keydown", (e) => {
    if (Saved && typeof Saved.isolateHeartKeyboard === "function") {
      Saved.isolateHeartKeyboard(e);
      return;
    }
    if (e.key === "Enter" || e.key === " ") {
      e.stopPropagation();
    }
  });

  favoriteBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    if (!Saved || !recordId) return;
    let nowSaved;
    if (Saved.has(recordId)) {
      Saved.remove(recordId);
      nowSaved = false;
    } else {
      const snapshot =
        typeof Saved.buildSnapshot === "function" ? Saved.buildSnapshot(d) : {};
      Saved.save({ id: recordId, snapshot: snapshot });
      nowSaved = true;
    }
    if (typeof Saved.applyHeartState === "function") {
      Saved.applyHeartState(favoriteBtn, nowSaved);
    }
  });
}

function existingDiamondIds() {
  const ids = new Set();
  allDiamonds.forEach((d) => {
    const id = diamondRecordId(d);
    if (id) ids.add(id);
  });
  return ids;
}

function getLoadMoreEls() {
  return {
    wrap: document.getElementById("diamondLoadMore"),
    btn: document.getElementById("diamondLoadMoreBtn"),
    error: document.getElementById("diamondLoadMoreError"),
  };
}

function setLoadMoreUi(mode) {
  const { wrap, btn, error } = getLoadMoreEls();
  if (!wrap || !btn) return;

  if (mode === "hidden") {
    wrap.hidden = true;
    btn.hidden = false;
    btn.disabled = false;
    btn.removeAttribute("aria-busy");
    btn.textContent = "Load More";
    if (error) error.hidden = true;
    return;
  }

  wrap.hidden = false;

  if (mode === "loading") {
    btn.hidden = false;
    btn.disabled = true;
    btn.setAttribute("aria-busy", "true");
    btn.textContent = "Loading…";
    if (error) error.hidden = true;
    return;
  }

  if (mode === "error") {
    btn.hidden = true;
    btn.disabled = false;
    btn.removeAttribute("aria-busy");
    btn.textContent = "Load More";
    if (error) error.hidden = false;
    return;
  }

  btn.hidden = false;
  btn.disabled = false;
  btn.removeAttribute("aria-busy");
  btn.textContent = "Load More";
  if (error) error.hidden = true;
}

function syncLoadMoreAfterResults() {
  const grid = getDiamondGrid();
  const state = grid && grid.getAttribute("data-results-state");
  if (state === "results" && hasMore) {
    setLoadMoreUi("ready");
    return;
  }
  setLoadMoreUi("hidden");
}

function bindDiamondLoadMore() {
  const { wrap } = getLoadMoreEls();
  if (!wrap || wrap.dataset.loadMoreBound === "true") return;
  wrap.dataset.loadMoreBound = "true";
  wrap.addEventListener("click", (e) => {
    const target = e.target && e.target.closest && e.target.closest(
      "#diamondLoadMoreBtn, [data-diamond-load-more-retry]"
    );
    if (!target) return;
    e.preventDefault();
    loadMoreDiamonds();
  });
}

async function reloadDiamonds(filters) {
  if (
    compareHoldsSearchGrid &&
    window.DiamondSearchCompare &&
    typeof window.DiamondSearchCompare.isActive === "function" &&
    window.DiamondSearchCompare.isActive() &&
    typeof window.DiamondSearchCompare.interceptReload === "function"
  ) {
    window.DiamondSearchCompare.interceptReload();
  }
  const requestId = ++reloadRequestId;
  if (reloadAbortController) {
    reloadAbortController.abort();
  }
  reloadAbortController =
    typeof AbortController === "function" ? new AbortController() : null;
  appendRequestId += 1;
  appendInFlight = false;
  allDiamonds = [];
  resultCount = null;
  hasMore = false;
  nextOffset = 0;
  // Measure the results column before Load More / chips / toolbar
  // can shrink it. Grid lock still happens inside the loading state.
  preserveDiamondResultsColumnHeight();
  setLoadMoreUi("hidden");
  searchFetchInFlight = true;
  if (!compareHoldsSearchGrid) {
    setDiamondResultsState("loading");
  }

  try {
    if (!window.IvanovAPI || typeof window.IvanovAPI.searchDiamonds !== "function") {
      throw new Error("window.IvanovAPI.searchDiamonds is not available.");
    }

    const nextFilters = Object.assign({}, filters || {}, {
      labgrown: getCurrentLabgrown(),
      offset: 0,
    });

    // Stamp titles from this request's origin, not UI state after await
    // (avoids a delayed Natural response using a newer Lab Grown selection).
    const requestOrigin = nextFilters.labgrown
      ? ORIGIN_LAB_GROWN
      : ORIGIN_NATURAL;

    const response = await window.IvanovAPI.searchDiamonds(
      nextFilters,
      reloadAbortController ? { signal: reloadAbortController.signal } : undefined
    );

    // Drop stale responses from earlier origin/filter changes.
    if (requestId !== reloadRequestId) return;
    searchFetchInFlight = false;

    const rawItems =
      response && response.diamonds_by_query && response.diamonds_by_query.items;
    if (!Array.isArray(rawItems)) {
      console.error("Error loading diamonds: unusable response.");
      if (compareHoldsSearchGrid) return;
      setDiamondResultsState("error");
      return;
    }

    const meta = readDiamondSearchMeta(response, 0);
    resultCount = meta.resultCount;
    hasMore = meta.hasMore;
    nextOffset = meta.nextOffset;

    allDiamonds = rawItems.map((item) => {
      const mapped = mapDiamondItem(item);
      mapped.origin = requestOrigin;
      return mapped;
    });
    if (compareHoldsSearchGrid) return;
    applyFilters();
  } catch (err) {
    // Intentional cancellation of an obsolete replace must not become
    // "Diamonds unavailable" or clear a newer successful grid.
    if (isIntentionalAbortError(err)) return;
    if (requestId !== reloadRequestId) return;
    searchFetchInFlight = false;
    console.error("Error loading diamonds:", err);
    if (compareHoldsSearchGrid) return;
    setDiamondResultsState("error");
  }
}

async function loadMoreDiamonds() {
  if (compareHoldsSearchGrid) return;
  if (appendInFlight || !hasMore) return;

  const grid = getDiamondGrid();
  if (!grid || grid.getAttribute("data-results-state") !== "results") return;

  const requestId = ++appendRequestId;
  const requestedOffset = nextOffset;
  appendInFlight = true;
  setLoadMoreUi("loading");

  try {
    if (!window.IvanovAPI || typeof window.IvanovAPI.searchDiamonds !== "function") {
      throw new Error("window.IvanovAPI.searchDiamonds is not available.");
    }

    const nextFilters = Object.assign({}, collectActiveServerFilters(), {
      labgrown: getCurrentLabgrown(),
      limit: DIAMOND_PAGE_SIZE,
      offset: requestedOffset,
    });

    const requestOrigin = nextFilters.labgrown
      ? ORIGIN_LAB_GROWN
      : ORIGIN_NATURAL;

    const response = await window.IvanovAPI.searchDiamonds(nextFilters);

    if (requestId !== appendRequestId) return;

    const rawItems =
      response && response.diamonds_by_query && response.diamonds_by_query.items;
    if (!Array.isArray(rawItems)) {
      console.error("Error loading more diamonds: unusable response.");
      setLoadMoreUi("error");
      return;
    }

    const meta = readDiamondSearchMeta(response, requestedOffset);
    if (meta.resultCount != null) resultCount = meta.resultCount;
    hasMore = meta.hasMore;
    nextOffset = meta.nextOffset;

    const seen = existingDiamondIds();
    const unique = [];
    rawItems.forEach((item) => {
      const mapped = mapDiamondItem(item);
      mapped.origin = requestOrigin;
      const id = diamondRecordId(mapped);
      if (id && seen.has(id)) return;
      if (id) seen.add(id);
      unique.push(mapped);
    });

    allDiamonds = allDiamonds.concat(unique);
    appendLiveDiamondResults(applyClientFilters(unique));
    syncLoadMoreAfterResults();
  } catch (err) {
    if (requestId !== appendRequestId) return;
    console.error("Error loading more diamonds:", err);
    setLoadMoreUi("error");
  } finally {
    if (requestId === appendRequestId) {
      appendInFlight = false;
    }
  }
}

// ————————————————————————————————————————————
// 2) Gather all active filters & re-render
// ————————————————————————————————————————————
function applyClientFilters(diamonds) {
  let filtered = [...diamonds];

  // Color range (D–K). Default full range = no Color filter.
  const colorRange = getColorRangeFromUi();
  if (colorRange && !isDefaultColorRange(colorRange.min, colorRange.max)) {
    const activeColors = expandColorRange(colorRange.min, colorRange.max);
    filtered = filtered.filter((d) => activeColors.includes(d.color));
  }

  // Clarity range (FL–SI2). Default full range = no Clarity filter.
  const clarityRange = getClarityRangeFromUi();
  if (clarityRange && !isDefaultClarityRange(clarityRange.min, clarityRange.max)) {
    const activeClarity = expandClarityRange(clarityRange.min, clarityRange.max);
    filtered = filtered.filter((d) => activeClarity.includes(d.clarity));
  }

  // Cut / Polish / Symmetry / Fluorescence remain server-side only
  // (API codes vs UI labels). Same skip as the previous applyFilters().

  // Certificate Lab (also filtered server-side via certificate_lab).
  const activeLab = getCertificateLabsFromUi();
  if (activeLab.length) {
    filtered = filtered.filter((d) =>
      activeLab.includes(normalizeCertificateLabValue(d.lab))
    );
  }

  // Carat range
  const minCarat = parseFloat(
    document.getElementById("caratMin").value
  ) || 0;
  const maxCarat = parseFloat(
    document.getElementById("caratMax").value
  ) || Infinity;
  filtered = filtered.filter(
    (d) => d.carat >= minCarat && d.carat <= maxCarat
  );

  // Price range
  const minPrice = parseFloat(
    document.getElementById("priceMin").value
  ) || 0;
  const maxPrice = parseFloat(
    document.getElementById("priceMax").value
  ) || Infinity;
  filtered = filtered.filter(
    (d) => d.retailPriceUsd >= minPrice && d.retailPriceUsd <= maxPrice
  );

  return filtered;
}

function applyFilters() {
  const colorRange = getColorRangeFromUi();
  let activeColors = [];
  if (colorRange && !isDefaultColorRange(colorRange.min, colorRange.max)) {
    activeColors = expandColorRange(colorRange.min, colorRange.max);
  }

  const clarityRange = getClarityRangeFromUi();
  let activeClarity = [];
  if (clarityRange && !isDefaultClarityRange(clarityRange.min, clarityRange.max)) {
    activeClarity = expandClarityRange(clarityRange.min, clarityRange.max);
  }

  const cutRange = getCutRangeFromUi();
  let activeCut = [];
  if (cutRange && !isDefaultCutRange(cutRange.min, cutRange.max)) {
    activeCut = expandCutRange(cutRange.min, cutRange.max);
  }

  const polishRange = getPolishRangeFromUi();
  let activePolish = [];
  if (polishRange && !isDefaultPolishRange(polishRange.min, polishRange.max)) {
    activePolish = expandPolishRange(polishRange.min, polishRange.max);
  }

  const symmetryRange = getSymmetryRangeFromUi();
  let activeSym = [];
  if (symmetryRange && !isDefaultSymmetryRange(symmetryRange.min, symmetryRange.max)) {
    activeSym = expandSymmetryRange(symmetryRange.min, symmetryRange.max);
  }

  const fluorescenceRange = getFluorescenceRangeFromUi();
  let activeFluorescence = [];
  if (
    fluorescenceRange &&
    !isDefaultFluorescenceRange(fluorescenceRange.min, fluorescenceRange.max)
  ) {
    activeFluorescence = expandFluorescenceRange(
      fluorescenceRange.min,
      fluorescenceRange.max
    );
  }

  const activeLab = getCertificateLabsFromUi();
  const minCarat = parseFloat(
    document.getElementById("caratMin").value
  ) || 0;
  const maxCarat = parseFloat(
    document.getElementById("caratMax").value
  ) || Infinity;
  const minPrice = parseFloat(
    document.getElementById("priceMin").value
  ) || 0;
  const maxPrice = parseFloat(
    document.getElementById("priceMax").value
  ) || Infinity;

  const filtered = applyClientFilters(allDiamonds);

  // ————————————————————————————————————————————
  // DEBUG LOG: see exactly which filters & count
  // ————————————————————————————————————————————
  console.log({
    colors: activeColors,
    clarity: activeClarity,
    cut: activeCut,
    polish: activePolish,
    symmetry: activeSym,
    fluorescence: activeFluorescence,
    lab: activeLab,
    caratRange: [minCarat, maxCarat],
    priceRange: [minPrice, maxPrice],
    resultCount: filtered.length,
  });

  if (!filtered.length) {
    setDiamondResultsState("empty");
    syncLoadMoreAfterResults();
    return;
  }

  setDiamondResultsState("results");
  renderLiveDiamondResults(filtered);
  syncLoadMoreAfterResults();
  scheduleReleaseDiamondResultsColumnHeight();
}

// ————————————————————————————————————————————
// 3) Render into your grid
// ————————————————————————————————————————————

// Display-only formatting helpers. These never change the underlying data -
// they only affect how existing fields are presented on the card.
const CUT_CODE_TO_LABEL = {
  ID: "Ideal",
  EX: "Excellent",
  VG: "Very Good",
  GD: "Good",
  FR: "Fair",
};

function escapeHtml(value) {
  return String(value == null ? "" : value).replace(/[&<>"']/g, (c) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
  ));
}

function toTitleCase(value) {
  return String(value)
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatCutLabel(cut) {
  if (!cut) return "";
  const label = CUT_CODE_TO_LABEL[String(cut).toUpperCase()];
  return label || toTitleCase(cut);
}

function isMeaningfulListValue(value) {
  const normalized = String(value == null ? "" : value).trim();
  if (!normalized) return false;
  const upper = normalized.toUpperCase();
  return upper !== "NONE" && upper !== "N/A" && upper !== "NA" && upper !== "-" && upper !== "\u2014";
}

function formatListLab(lab) {
  return isMeaningfulListValue(lab) ? String(lab).trim() : "";
}

function formatCarat(carat) {
  const n = Number(carat);
  return Number.isFinite(n) ? n.toFixed(2) : "";
}

function formatDiamondPrice(retailPriceUsd) {
  const rawPrice = Number(retailPriceUsd);
  if (!Number.isFinite(rawPrice)) return "";

  // Backend retailPriceUsd is already authoritative whole-dollar retail.
  // Format only — no cents conversion and no pricing formula here.
  let displayAmount = rawPrice;
  if (window.Currency && typeof window.Currency.convert === "function") {
    try {
      const converted = Number(window.Currency.convert(rawPrice));
      if (Number.isFinite(converted)) {
        displayAmount = converted;
      }
    } catch (err) {
      // Ignore currency helper failures - fall back to rawPrice above.
    }
  }

  return `$${Math.round(displayAmount).toLocaleString("en-US")}`;
}

function buildDiamondTitle(d) {
  const caratLabel = formatCarat(d.carat);
  const shapeLabel = d.shape ? toTitleCase(d.shape) : "";
  const originLabel =
    d.origin === ORIGIN_LAB_GROWN ? "Lab Grown Diamond" : "Natural Diamond";
  return [caratLabel && `${caratLabel} ct`, shapeLabel, originLabel]
    .filter(Boolean)
    .join(" ");
}

function buildDiamondSpecLine(d) {
  return [formatCutLabel(d.cut), d.color, d.clarity]
    .filter(Boolean)
    .join(" \u00b7 ");
}

// Fired when a card (or its media / keyboard) is activated. Opens Quick Look
// Viewer via diamond:select. The View Diamond CTA is a real Detail link and
// must not dispatch this event.
function activateDiamondCard(d) {
  document.dispatchEvent(new CustomEvent("diamond:select", { detail: d }));
}

function createDiamondCard(d) {
    const title = buildDiamondTitle(d);
    const specLine = buildDiamondSpecLine(d);
    const priceLabel = formatDiamondPrice(d.retailPriceUsd);
    const caratLabel = formatCarat(d.carat);
    const cutLabel = formatCutLabel(d.cut);

    // Media data + rendering are owned by the Media Slot (diamond-card-media.js),
    // not by this card template — see that file for why. The card only
    // decides *where* the slot's markup goes and passes it the alt text.
    const media = DiamondCardMedia.resolveDiamondMedia(d);
    const mediaHtml = DiamondCardMedia.buildDiamondCardMedia(media, { alt: title });
    const has360 = DiamondCardMedia.hasInteractive360(media);
    const listMeta = [formatListLab(d.lab), has360 ? "360\u00b0" : ""]
      .filter(Boolean)
      .join(" \u2022 ");
    const listDash = "\u2014";
    const listCut = isMeaningfulListValue(cutLabel) ? cutLabel : listDash;
    const listColor = isMeaningfulListValue(d.color) ? d.color : listDash;
    const listClarity = isMeaningfulListValue(d.clarity) ? d.clarity : listDash;
    const compactSpecs = [listColor, listClarity, formatListLab(d.lab)]
      .filter(isMeaningfulListValue)
      .join(" \u2022 ");
    const compactSize =
      listCut !== listDash
        ? [caratLabel, listCut].filter(Boolean).join(" \u2022 ")
        : "";

    const card = document.createElement("div");
    card.className = "diamond-card";
    // Do not set role="button" on this wrapper. A button role makes
    // descendants presentational, so Tab skips the native heart (and CTA).
    // tabindex="0" + keydown keep the card itself keyboard-activatable.
    card.setAttribute("tabindex", "0");
    card.setAttribute("aria-label", title);
    const recordId = diamondRecordId(d);
    if (recordId) card.dataset.diamondId = recordId;
    card.innerHTML = `
      <div class="diamond-card__media">
        ${mediaHtml}
        <button type="button" class="diamond-card__favorite" aria-label="Save diamond" aria-pressed="false">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z"></path>
          </svg>
        </button>
      </div>
      <div class="diamond-card__body">
        <div class="diamond-card__info">
          <h3 class="diamond-card__title heading-static">${escapeHtml(title)}</h3>
          ${compactSpecs ? `<p class="diamond-card__compact-specs">${escapeHtml(compactSpecs)}</p>` : ""}
          ${compactSize ? `<p class="diamond-card__compact-size">${escapeHtml(compactSize)}</p>` : ""}
          ${specLine ? `<p class="diamond-card__specs">${escapeHtml(specLine)}</p>` : ""}
          ${listMeta ? `<p class="diamond-card__list-meta">${escapeHtml(listMeta)}</p>` : ""}
        </div>
        <div class="diamond-card__list-compare">
          <span class="diamond-card__list-col diamond-card__list-col--price">${escapeHtml(priceLabel || listDash)}</span>
          <span class="diamond-card__list-col diamond-card__list-col--carat">${escapeHtml(caratLabel || listDash)}</span>
          <span class="diamond-card__list-col diamond-card__list-col--cut">${escapeHtml(listCut)}</span>
          <span class="diamond-card__list-col diamond-card__list-col--color">${escapeHtml(listColor)}</span>
          <span class="diamond-card__list-col diamond-card__list-col--clarity">${escapeHtml(listClarity)}</span>
        </div>
        <div class="diamond-card__footer">
          <span class="diamond-card__price">${escapeHtml(priceLabel)}</span>
          <a class="diamond-card__cta" href="${escapeHtml(window.IvanovAPI.buildDiamondDetailUrl(recordId))}">View Diamond</a>
        </div>
      </div>
    `;

    card.addEventListener("click", (e) => {
      if (isDiamondFavoriteEventTarget(e.target)) return;
      if (isDiamondDetailCtaEventTarget(e.target)) return;
      activateDiamondCard(d);
    });
    card.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        if (isDiamondFavoriteEventTarget(e.target)) return;
        if (isDiamondDetailCtaEventTarget(e.target)) return;
        e.preventDefault();
        activateDiamondCard(d);
      }
    });

    const detailCta = card.querySelector(".diamond-card__cta");
    if (detailCta) {
      detailCta.style.textDecoration = "none";
      detailCta.addEventListener("click", (e) => {
        e.stopPropagation();
      });
    }

    const favoriteBtn = card.querySelector(".diamond-card__favorite");
    bindDiamondCardFavorite(favoriteBtn, d);

    // Media interaction (today: broken-image -> placeholder fallback) is
    // also owned by the slot, keyed off the same media descriptor used to
    // render it.
    const mediaSlotEl = card.querySelector(".diamond-card__media");
    DiamondCardMedia.attachDiamondMediaInteractions(mediaSlotEl, media, { alt: title });

    return card;
}

function renderLiveDiamondResults(list) {
  const grid = getDiamondGrid();
  if (!grid) return;

  if (!list || !list.length) {
    setDiamondResultsState("empty");
    return;
  }

  grid.innerHTML = "";
  list.forEach((d) => {
    grid.appendChild(createDiamondCard(d));
  });
  releaseDiamondGridHeight(grid);
}

function appendLiveDiamondResults(list) {
  const grid = getDiamondGrid();
  if (!grid || !list || !list.length) return;
  list.forEach((d) => {
    grid.appendChild(createDiamondCard(d));
  });
}

// ————————————————————————————————————————————
// 4) Wire all your UI controls to call applyFilters()
// ————————————————————————————————————————————
function collectActiveServerFilters() {
  const filters = { limit: DIAMOND_PAGE_SIZE };
  const currentShape = shapeSlugToLabel(
    document.querySelector(".shape-scroll-container button.active")?.dataset
      .shape
  );

  if (currentShape) filters.shape = currentShape;

  // Color → existing backend `color` query (comma-separated list for ranges).
  // Omit when UI is at the default D–K (no Color filter).
  const colorRange = getColorRangeFromUi();
  if (colorRange && !isDefaultColorRange(colorRange.min, colorRange.max)) {
    const colors = expandColorRange(colorRange.min, colorRange.max);
    filters.color = colors.join(",");
  }

  // Clarity → existing backend `clarity` query (comma-separated list for ranges).
  // Omit when UI is at the default FL–SI2 (no Clarity filter).
  const clarityRange = getClarityRangeFromUi();
  if (clarityRange && !isDefaultClarityRange(clarityRange.min, clarityRange.max)) {
    const clarities = expandClarityRange(clarityRange.min, clarityRange.max);
    filters.clarity = clarities.join(",");
  }

  // Cut → existing backend `cut` query (comma-separated API codes for ranges).
  // Omit when UI is at the default Fair–Ideal (no Cut filter).
  const cutRange = getCutRangeFromUi();
  if (cutRange && !isDefaultCutRange(cutRange.min, cutRange.max)) {
    const cuts = expandCutRange(cutRange.min, cutRange.max)
      .map((label) => CUT_LABEL_TO_API_VALUE[label] || label);
    filters.cut = cuts.join(",");
  }

  // Polish → existing backend `polish` query (comma-separated API codes for ranges).
  // Omit when UI is at the default Good–Excellent (no Polish filter).
  const polishRange = getPolishRangeFromUi();
  if (polishRange && !isDefaultPolishRange(polishRange.min, polishRange.max)) {
    const polishes = expandPolishRange(polishRange.min, polishRange.max)
      .map((label) => POLISH_LABEL_TO_API_VALUE[label] || label);
    filters.polish = polishes.join(",");
  }

  // Symmetry → existing backend `symmetry` query (comma-separated API codes for ranges).
  // Omit when UI is at the default Good–Excellent (no Symmetry filter).
  const symmetryRange = getSymmetryRangeFromUi();
  if (symmetryRange && !isDefaultSymmetryRange(symmetryRange.min, symmetryRange.max)) {
    const symmetries = expandSymmetryRange(symmetryRange.min, symmetryRange.max)
      .map((label) => SYMMETRY_LABEL_TO_API_VALUE[label] || label);
    filters.symmetry = symmetries.join(",");
  }

  // Fluorescence → existing backend `fluorescence` query (comma-separated API codes for ranges).
  // Omit when UI is at the default Very Strong–None (no Fluorescence filter).
  const fluorescenceRange = getFluorescenceRangeFromUi();
  if (
    fluorescenceRange &&
    !isDefaultFluorescenceRange(fluorescenceRange.min, fluorescenceRange.max)
  ) {
    const fluorescences = fluorescenceRangeToApiValues(
      fluorescenceRange.min,
      fluorescenceRange.max
    );
    filters.fluorescence = fluorescences.join(",");
  }

  // Certificate Lab → existing backend `certificate_lab` query (comma-separated).
  // Omit when nothing is selected (no Certificate Lab filter).
  const certificateLabs = getCertificateLabsFromUi();
  if (certificateLabs.length) {
    filters.certificate_lab = certificateLabs.join(",");
  }

  // Geometry ranges → confirmed backend query params (ratioMin/Max,
  // tableMin/Max, depthMin/Max, crownAngleMin/Max, pavilionAngleMin/Max,
  // starLengthMin/Max, lowerHalfMin/Max). Omit each pair when the UI is at
  // its default full-bound range (no filter for that control).
  GEOMETRY_RANGE_CONFIGS.forEach((cfg) => {
    const range = geometryRangeControllers[cfg.key].getRangeFromUi();
    if (range && !geometryRangeControllers[cfg.key].isDefaultRange(range.min, range.max)) {
      filters[cfg.minParam] = range.min;
      filters[cfg.maxParam] = range.max;
    }
  });

  // Girdle → confirmed backend `girdle` query (comma-separated short codes).
  // Omit when nothing is selected (no Girdle filter).
  const girdleLabels = getGirdleFromUi();
  if (girdleLabels.length) {
    filters.girdle = girdleLabels
      .map((label) => GIRDLE_LABEL_TO_API_VALUE[label] || label)
      .join(",");
  }

  // Culet → confirmed backend `culet` query (comma-separated short codes).
  // Omit when nothing is selected (no Culet filter).
  const culetLabels = getCuletFromUi();
  if (culetLabels.length) {
    filters.culet = culetLabels
      .map((label) => CULET_LABEL_TO_API_VALUE[label] || label)
      .join(",");
  }

  // Media → confirmed backend has_image / has_v360 / has_certpdf query
  // params. Each toggle is fully independent; omit a param entirely when
  // its own toggle is OFF. Images ON (product default) sends has_image=true;
  // Images OFF omits has_image — never set has_image=false. There is no
  // combined "media" param and no standalone Video filter - never add has_video.
  if (getMediaHasImageFromUi()) filters.has_image = true;
  if (getMediaHasV360FromUi()) filters.has_v360 = true;
  if (getMediaHasCertPdfFromUi()) filters.has_certpdf = true;

  // Availability → confirmed backend is_fbn / returns / has_tracer boolean
  // query params. Each toggle is fully independent; omit a param entirely
  // when its own toggle is OFF (no filter for that flag).
  if (getAvailabilityIsFbnFromUi()) filters.is_fbn = true;
  if (getAvailabilityReturnsFromUi()) filters.returns = true;
  if (getAvailabilityHasTracerFromUi()) filters.has_tracer = true;

  // Estimated Delivery → customer-facing deliveryTime (7/10/14 business
  // days) mapped to Nivoda supplier-to-Ivanov delivery_time
  // thresholds/windows (2/6/10) only right here, immediately before the
  // request goes out. Omit the param entirely for "Any Date" (no filter
  // for that flag).
  const availabilityDeliveryTime = getAvailabilityDeliveryTimeFromUi();
  if (
    availabilityDeliveryTime &&
    AVAILABILITY_DELIVERY_TIME_TO_API_VALUE[availabilityDeliveryTime]
  ) {
    filters.delivery_time =
      AVAILABILITY_DELIVERY_TIME_TO_API_VALUE[availabilityDeliveryTime];
  }

  // Carat -> existing backend query params caratMin / caratMax.
  // Always send the effective visible range, including the default
  // 0.25–50.00. Nivoda must sort inside the same bounds applyFilters()
  // uses; omitting defaults let a short page return out-of-range stones
  // that the client then dropped. The storefront URL still omits
  // default Carat via syncCaratUrl.
  const minCarat = parseFloat(
    document.getElementById("caratMin")?.value
  );
  const maxCarat = parseFloat(
    document.getElementById("caratMax")?.value
  );
  if (Number.isFinite(minCarat) && Number.isFinite(maxCarat)) {
    filters.caratMin = minCarat;
    filters.caratMax = maxCarat;
  } else {
    filters.caratMin = CARAT_DEFAULT_MIN;
    filters.caratMax = CARAT_DEFAULT_MAX;
  }

  // Price -> Ivanov retail USD query params priceMin / priceMax.
  // Backend inverts these to Nivoda cost (dollar_value) and validates retail.
  // Omit each bound when empty/default (placeholder-only inputs).
  const minPrice = parseFloat(
    document.getElementById("priceMin")?.value
  );
  const maxPrice = parseFloat(
    document.getElementById("priceMax")?.value
  );
  if (Number.isFinite(minPrice)) filters.priceMin = minPrice;
  if (Number.isFinite(maxPrice)) filters.priceMax = maxPrice;

  // Sort → existing backend `sort` query. Featured is the default: omit
  // the param (never send `sort=featured`) so the backend omits Nivoda
  // order. Order comes back in items; do not Array.sort() on the client.
  const sort = getSortFromUi();
  if (sort) filters.sort = sort;

  return filters;
}

// Carat replace search. Only runs on commit (slider release, Popular,
// typed Exact Range commit, Reset). Slider drag does not call this.
// reloadRequestId still drops any stale responses.
let caratReloadScheduled = false;
let caratReloadTimer = null;
function cancelPendingCaratReload() {
  if (caratReloadTimer != null) {
    clearTimeout(caratReloadTimer);
    caratReloadTimer = null;
  }
  caratReloadScheduled = false;
}
function scheduleCaratReload() {
  if (caratReloadScheduled) return;
  caratReloadScheduled = true;
  caratReloadTimer = setTimeout(() => {
    caratReloadScheduled = false;
    caratReloadTimer = null;
    const minCarat = parseFloat(
      document.getElementById("caratMin")?.value
    );
    const maxCarat = parseFloat(
      document.getElementById("caratMax")?.value
    );
    // Skip incomplete typing; wait until both bounds are valid numbers.
    if (!Number.isFinite(minCarat) || !Number.isFinite(maxCarat)) return;
    // Keep caratMin/caratMax in the URL; leave origin/shape/etc. untouched.
    syncCaratUrlFromUi(minCarat, maxCarat);
    reloadDiamonds(collectActiveServerFilters());
  }, 0);
}
window.scheduleCaratReload = scheduleCaratReload;
window.cancelPendingCaratReload = cancelPendingCaratReload;

// Coalesce dual priceMin+priceMax "input" events (chip clear emits both)
// into one request; reloadRequestId still drops any stale responses.
let priceReloadScheduled = false;
function schedulePriceReload() {
  if (priceReloadScheduled) return;
  priceReloadScheduled = true;
  setTimeout(() => {
    priceReloadScheduled = false;
    const minPrice = parseFloat(
      document.getElementById("priceMin")?.value
    );
    const maxPrice = parseFloat(
      document.getElementById("priceMax")?.value
    );
    // Both empty → clear priceMin/priceMax; leave origin/shape/carat untouched.
    // Both valid → keep them in the URL. One-sided typing leaves URL as-is.
    if (!Number.isFinite(minPrice) && !Number.isFinite(maxPrice)) {
      syncPriceUrlFromUi(null, null);
    } else if (Number.isFinite(minPrice) && Number.isFinite(maxPrice)) {
      syncPriceUrlFromUi(minPrice, maxPrice);
    }
    reloadDiamonds(collectActiveServerFilters());
  }, 0);
}

// Coalesce dual colorMin+colorMax "input" events (slider/letters emit both)
// into one request; reloadRequestId still drops any stale responses.
let colorReloadScheduled = false;
function scheduleColorReload() {
  if (colorReloadScheduled) return;
  colorReloadScheduled = true;
  setTimeout(() => {
    colorReloadScheduled = false;
    const range = getColorRangeFromUi();
    // Skip incomplete typing; wait until both bounds are valid letters.
    if (!range) return;
    // Keep color / colorMin / colorMax in the URL; leave other filters untouched.
    syncColorUrlFromUi(range.min, range.max);
    reloadDiamonds(collectActiveServerFilters());
  }, 0);
}

// Coalesce dual clarityMin+clarityMax "input" events (slider/labels emit both)
// into one request; reloadRequestId still drops any stale responses.
let clarityReloadScheduled = false;
function scheduleClarityReload() {
  if (clarityReloadScheduled) return;
  clarityReloadScheduled = true;
  setTimeout(() => {
    clarityReloadScheduled = false;
    const range = getClarityRangeFromUi();
    // Skip incomplete typing; wait until both bounds are valid grades.
    if (!range) return;
    // Keep clarity / clarityMin / clarityMax in the URL; leave other filters untouched.
    syncClarityUrlFromUi(range.min, range.max);
    reloadDiamonds(collectActiveServerFilters());
  }, 0);
}

// Coalesce dual cutMin+cutMax "input" events (slider/labels emit both)
// into one request; reloadRequestId still drops any stale responses.
let cutReloadScheduled = false;
function scheduleCutReload() {
  if (cutReloadScheduled) return;
  cutReloadScheduled = true;
  setTimeout(() => {
    cutReloadScheduled = false;
    const range = getCutRangeFromUi();
    // Skip until both bounds are valid grades.
    if (!range) return;
    // Keep cut / cutMin / cutMax in the URL; leave other filters untouched.
    syncCutUrlFromUi(range.min, range.max);
    reloadDiamonds(collectActiveServerFilters());
  }, 0);
}

// Coalesce dual polishMin+polishMax "input" events (slider/labels emit both)
// into one request; reloadRequestId still drops any stale responses.
let polishReloadScheduled = false;
function schedulePolishReload() {
  if (polishReloadScheduled) return;
  polishReloadScheduled = true;
  setTimeout(() => {
    polishReloadScheduled = false;
    const range = getPolishRangeFromUi();
    // Skip until both bounds are valid grades.
    if (!range) return;
    // Keep polish / polishMin / polishMax in the URL; leave other filters untouched.
    syncPolishUrlFromUi(range.min, range.max);
    reloadDiamonds(collectActiveServerFilters());
  }, 0);
}

// Coalesce dual symmetryMin+symmetryMax "input" events (slider/labels emit both)
// into one request; reloadRequestId still drops any stale responses.
let symmetryReloadScheduled = false;
function scheduleSymmetryReload() {
  if (symmetryReloadScheduled) return;
  symmetryReloadScheduled = true;
  setTimeout(() => {
    symmetryReloadScheduled = false;
    const range = getSymmetryRangeFromUi();
    // Skip until both bounds are valid grades.
    if (!range) return;
    // Keep symmetry / symmetryMin / symmetryMax in the URL; leave other filters untouched.
    syncSymmetryUrlFromUi(range.min, range.max);
    reloadDiamonds(collectActiveServerFilters());
  }, 0);
}

// Coalesce dual fluorescenceMin+fluorescenceMax "input" events (slider/labels emit both)
// into one request; reloadRequestId still drops any stale responses.
let fluorescenceReloadScheduled = false;
function scheduleFluorescenceReload() {
  if (fluorescenceReloadScheduled) return;
  fluorescenceReloadScheduled = true;
  setTimeout(() => {
    fluorescenceReloadScheduled = false;
    const range = getFluorescenceRangeFromUi();
    // Skip until both bounds are valid intensities.
    if (!range) return;
    // Keep fluorescence / fluorescenceMin / fluorescenceMax in the URL; leave other filters untouched.
    syncFluorescenceUrlFromUi(range.min, range.max);
    reloadDiamonds(collectActiveServerFilters());
  }, 0);
}

function initListeners() {
  bindDiamondResultsRetry();
  bindDiamondLoadMore();

  // Diamond Origin (single-select): Natural -> labgrown:false, Lab Grown -> labgrown:true
  document.querySelectorAll("#originOptions button[data-origin]").forEach((button) => {
    button.addEventListener("click", () => {
      const origin = setOriginUi(button.getAttribute("data-origin"));

      syncOriginUrl(origin, true);

      // Preserve other active server filters; only origin/labgrown changes.
      const filters = collectActiveServerFilters();
      filters.labgrown = origin === ORIGIN_LAB_GROWN;
      reloadDiamonds(filters);
    });
  });

  // Shape buttons — single-select across Primary + More Shapes.
  // Preserve Origin (via reloadDiamonds) + Carat/etc.
  document
    .querySelectorAll(".shape-scroll-container button")
    .forEach((btn) => {
      btn.addEventListener("click", () => {
        setShapeUi(btn.dataset.shape);

        // Shape only in the URL; leave origin/carat/etc. untouched.
        syncShapeUrl(btn.dataset.shape, false);

        reloadDiamonds(collectActiveServerFilters());
      });
    });

  // Color Min/Max (also driven by dual-range handles + letter labels via
  // synthetic "input" events). Reloads with color / expanded color list.
  ["colorMin", "colorMax"].forEach((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.addEventListener("input", scheduleColorReload);
  });

  // Clarity Min/Max (also driven by dual-range handles + grade labels via
  // synthetic "input" events). Reloads with clarity / expanded clarity list.
  ["clarityMin", "clarityMax"].forEach((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.addEventListener("input", scheduleClarityReload);
  });

  // Cut Min/Max (hidden; driven by dual-range handles + grade labels via
  // synthetic "input" events). Reloads with cut / expanded cut code list.
  ["cutMin", "cutMax"].forEach((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.addEventListener("input", scheduleCutReload);
  });

  // Polish Min/Max (hidden; driven by dual-range handles + grade labels via
  // synthetic "input" events). Reloads with polish / expanded polish code list.
  ["polishMin", "polishMax"].forEach((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.addEventListener("input", schedulePolishReload);
  });

  // Symmetry Min/Max (hidden; driven by dual-range handles + grade labels via
  // synthetic "input" events). Reloads with symmetry / expanded symmetry code list.
  ["symmetryMin", "symmetryMax"].forEach((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.addEventListener("input", scheduleSymmetryReload);
  });

  // Fluorescence Min/Max (hidden; driven by dual-range handles + intensity labels via
  // synthetic "input" events). Reloads with fluorescence / expanded fluorescence code list.
  ["fluorescenceMin", "fluorescenceMax"].forEach((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.addEventListener("input", scheduleFluorescenceReload);
  });

  // Certificate Lab (multi-select): GIA / IGI / GCAL → certificate_lab
  document.querySelectorAll("#labOptions span[data-certificate-lab]").forEach((span) => {
    span.addEventListener("click", () => {
      span.classList.toggle("active");
      const labs = getCertificateLabsFromUi();
      // Certificate Lab only in the URL; leave origin/shape/etc. untouched.
      syncCertificateLabUrl(labs, false);
      reloadDiamonds(collectActiveServerFilters());
    });
  });

  // Geometry ranges — L/W Ratio, Table %, Depth %, Crown Angle, Pavilion
  // Angle, Star Length, Lower Half (each driven by its own dual-range slider
  // + Min/Max via synthetic "input" events, same wiring as Carat/Price).
  GEOMETRY_RANGE_CONFIGS.forEach((cfg) => {
    const controller = geometryRangeControllers[cfg.key];
    [cfg.key + "Min", cfg.key + "Max"].forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      el.addEventListener("input", controller.scheduleReload);
    });
  });

  // Girdle (multi-select): same pattern as Certificate Lab.
  document.querySelectorAll("#girdleOptions span[data-girdle]").forEach((span) => {
    span.addEventListener("click", () => {
      span.classList.toggle("active");
      const labels = getGirdleFromUi();
      // Girdle only in the URL; leave origin/shape/etc. untouched.
      syncGirdleUrl(labels, false);
      reloadDiamonds(collectActiveServerFilters());
    });
  });

  // Culet (multi-select): same pattern as Girdle/Certificate Lab.
  document.querySelectorAll("#culetOptions span[data-culet]").forEach((span) => {
    span.addEventListener("click", () => {
      span.classList.toggle("active");
      const labels = getCuletFromUi();
      // Culet only in the URL; leave origin/shape/etc. untouched.
      syncCuletUrl(labels, false);
      reloadDiamonds(collectActiveServerFilters());
    });
  });

  // Media (three independent boolean toggles): Images / 360° / Certificate
  // PDF. Each checkbox only ever syncs and reloads with its own toggle's
  // controller - never touches the other two toggles or any other filter.
  [
    { id: "mediaHasImage", getFromUi: getMediaHasImageFromUi, syncUrl: syncMediaHasImageUrl },
    { id: "mediaHasV360", getFromUi: getMediaHasV360FromUi, syncUrl: syncMediaHasV360Url },
    { id: "mediaHasCertPdf", getFromUi: getMediaHasCertPdfFromUi, syncUrl: syncMediaHasCertPdfUrl },
  ].forEach((toggle) => {
    const el = document.getElementById(toggle.id);
    if (!el) return;
    el.addEventListener("change", () => {
      toggle.syncUrl(toggle.getFromUi(), false);
      reloadDiamonds(collectActiveServerFilters());
    });
  });

  // Availability (three independent boolean toggles): Faster Fulfillment /
  // Returnable Only / Traced Diamonds Only. Same wiring as Media's toggles -
  // each checkbox only ever syncs and reloads with its own toggle's controller.
  [
    { id: "availabilityIsFbn", getFromUi: getAvailabilityIsFbnFromUi, syncUrl: syncAvailabilityIsFbnUrl },
    { id: "availabilityReturns", getFromUi: getAvailabilityReturnsFromUi, syncUrl: syncAvailabilityReturnsUrl },
    { id: "availabilityHasTracer", getFromUi: getAvailabilityHasTracerFromUi, syncUrl: syncAvailabilityHasTracerUrl },
  ].forEach((toggle) => {
    const el = document.getElementById(toggle.id);
    if (!el) return;
    el.addEventListener("change", () => {
      toggle.syncUrl(toggle.getFromUi(), false);
      reloadDiamonds(collectActiveServerFilters());
    });
  });

  // Estimated Delivery (single-select radio): Any Date / Within 7 / 10 / 14
  // Business Days. Only ever syncs/reloads this control's own param - the
  // internal Nivoda delivery_time mapping happens later, in
  // collectActiveServerFilters, never here.
  document
    .querySelectorAll(`input[name="${AVAILABILITY_DELIVERY_TIME_RADIO_NAME}"]`)
    .forEach((radio) => {
      radio.addEventListener("change", () => {
        if (!radio.checked) return;
        syncAvailabilityDeliveryTimeUrl(getAvailabilityDeliveryTimeFromUi(), false);
        reloadDiamonds(collectActiveServerFilters());
      });
    });

  // Carat Exact Range number fields. Typed keystrokes do not search;
  // liquid commitNumbers / Popular / slider change call scheduleCaratReload
  // after a committed canonical range. Do not bind "input" here.

  // Price Min/Max. Reloads from the server with priceMin/priceMax.
  ["priceMin", "priceMax"].forEach((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.addEventListener("input", schedulePriceReload);
  });

  // Sort (toolbar). Featured removes `sort` from the URL and the
  // request; any other allowed value pushes `sort=<value>` and reloads
  // through the same pipeline as Shape / Origin / filters. Does not
  // reset other filters. Grid/List is a separate localStorage preference.
  const sortEl = document.getElementById(SORT_ELEMENT_ID);
  if (sortEl) {
    sortEl.addEventListener("change", () => {
      syncSortUrl(getSortFromUi(), false);
      reloadDiamonds(collectActiveServerFilters());
    });
  }
}

// ————————————————————————————————————————————
// Search Compare availability (foundation).
// Saved ids stay in window.DiamondSaved. This map only remembers the
// last confirmed batch status so Compare (N) can exclude diamonds that
// are not currently available. It does not render Compare mode.
// ————————————————————————————————————————————
const compareAvailabilityById = Object.create(null);
let compareBatchAbortController = null;
let compareBatchGeneration = 0;
let searchCompareListenersBound = false;

function mapShopperV360(v360) {
  if (!v360 || typeof v360 !== "object") return null;
  const url = typeof v360.url === "string" ? v360.url.trim() : "";
  if (!url) return null;
  const frameCount = v360.frame_count != null ? v360.frame_count : v360.frameCount;
  const topIndex = v360.top_index != null ? v360.top_index : v360.topIndex;
  const media = {
    url: url,
    frame_count: frameCount != null ? frameCount : null,
    top_index: topIndex != null ? topIndex : null,
  };
  if (Array.isArray(v360.frames)) media.frames = v360.frames.slice();
  return media;
}

// ShopperDiamond (POST /diamonds/batch and GET /diamonds/{id}) → the flat
// object createDiamondCard already renders. Does not call mapDiamondItem.
function mapShopperDiamondToSearchCard(diamond) {
  if (!diamond || typeof diamond !== "object") return null;
  const cert =
    diamond.certificate && typeof diamond.certificate === "object"
      ? diamond.certificate
      : {};
  const media =
    diamond.media && typeof diamond.media === "object" ? diamond.media : {};
  const image =
    typeof media.image === "string" && media.image.trim() ? media.image.trim() : null;
  const certificateNumber =
    cert.number == null || cert.number === "" ? null : String(cert.number);
  const certificateUrl =
    cert.url == null || String(cert.url).trim() === "" ? null : String(cert.url);
  let availability = null;
  if (diamond.availability === "available" || diamond.availability === "unavailable") {
    availability = diamond.availability;
  }
  return {
    id: diamond.id == null || diamond.id === "" ? "" : String(diamond.id).trim(),
    origin: diamond.origin == null || diamond.origin === "" ? null : diamond.origin,
    shape: diamond.shape == null || diamond.shape === "" ? null : diamond.shape,
    carat: diamond.carat == null || diamond.carat === "" ? null : diamond.carat,
    cut: diamond.cut == null || diamond.cut === "" ? null : diamond.cut,
    color: diamond.color == null || diamond.color === "" ? null : diamond.color,
    clarity: diamond.clarity == null || diamond.clarity === "" ? null : diamond.clarity,
    lab: diamond.lab == null || diamond.lab === "" ? null : diamond.lab,
    certificateNumber: certificateNumber,
    certificateUrl: certificateUrl,
    hasGradingReport: cert.hasGradingReport === true,
    retailPriceUsd: diamond.retailPriceUsd,
    image: image,
    v360: mapShopperV360(media.v360),
    preparedTurntable:
      media.preparedTurntable && typeof media.preparedTurntable === "object"
        ? media.preparedTurntable
        : null,
    availability: availability,
  };
}

function canonicalCompareId(id) {
  const Saved = window.DiamondSaved;
  if (Saved && typeof Saved.normalizeId === "function") {
    try {
      return Saved.normalizeId(id) || "";
    } catch (err) {
      return "";
    }
  }
  if (id == null) return "";
  const raw = String(id).trim();
  return raw;
}

function listSavedCompareIds() {
  const Saved = window.DiamondSaved;
  if (!Saved || typeof Saved.list !== "function") return [];
  let items = [];
  try {
    items = Saved.list() || [];
  } catch (err) {
    return [];
  }
  const ids = [];
  const seen = Object.create(null);
  items.forEach((item) => {
    const id = canonicalCompareId(item && item.id);
    if (!id || seen[id]) return;
    seen[id] = true;
    ids.push(id);
  });
  return ids;
}

function isConfirmedCompareStatus(status) {
  return (
    status === "available" ||
    status === "unavailable" ||
    status === "not_found" ||
    status === "invalid"
  );
}

function pruneCompareAvailability() {
  const keep = Object.create(null);
  listSavedCompareIds().forEach((id) => {
    keep[id] = true;
  });
  Object.keys(compareAvailabilityById).forEach((id) => {
    if (!keep[id]) delete compareAvailabilityById[id];
  });
}

function unclassifiedCompareIds() {
  return listSavedCompareIds().filter((id) => {
    const entry = compareAvailabilityById[id];
    return !(entry && entry.confirmed && isConfirmedCompareStatus(entry.status));
  });
}

function compareAvailableCount() {
  let count = 0;
  listSavedCompareIds().forEach((id) => {
    const entry = compareAvailabilityById[id];
    if (entry && entry.confirmed && entry.status === "available" && entry.diamond) {
      count += 1;
    }
  });
  return count;
}

function listAvailableCompareDiamonds() {
  const diamonds = [];
  listSavedCompareIds().forEach((id) => {
    const entry = compareAvailabilityById[id];
    if (entry && entry.confirmed && entry.status === "available" && entry.diamond) {
      diamonds.push(entry.diamond);
    }
  });
  return diamonds;
}

function notifyCompareAvailability() {
  const compare = window.DiamondSearchCompare;
  if (!compare || typeof compare.onAvailability !== "function") return;
  try {
    compare.onAvailability();
  } catch (err) {
    // Compare mode must not break availability updates.
  }
}

function syncCompareControl() {
  const count = compareAvailableCount();
  const button = document.getElementById("diamondSearchCompare");
  const compare = window.DiamondSearchCompare;
  const inMode = !!(compare && typeof compare.isActive === "function" && compare.isActive());
  if (button) {
    const label = "Compare (" + count + ")";
    const text =
      typeof button.querySelector === "function"
        ? button.querySelector("[data-compare-label]")
        : null;
    if (text) text.textContent = label;
    const noun = count === 1 ? "available saved diamond" : "available saved diamonds";
    if (typeof button.setAttribute === "function") {
      button.setAttribute("aria-label", "Compare " + count + " " + noun);
    }
    if (count < 1 || inMode) {
      button.hidden = true;
      if (typeof button.setAttribute === "function") {
        button.setAttribute("aria-hidden", "true");
      }
    } else {
      button.hidden = false;
      if (typeof button.removeAttribute === "function") {
        button.removeAttribute("aria-hidden");
      }
    }
  }
  if (!syncCompareControl.notifying) {
    syncCompareControl.notifying = true;
    try {
      notifyCompareAvailability();
    } finally {
      syncCompareControl.notifying = false;
    }
  }
  return count;
}

function applyCompareBatchRow(row) {
  if (!row || typeof row !== "object") return;
  const id = canonicalCompareId(row.id);
  if (!id) return;
  if (listSavedCompareIds().indexOf(id) === -1) return;
  const status = row.status;
  if (status === "available") {
    const mapped = mapShopperDiamondToSearchCard(row.diamond);
    if (!mapped) return;
    mapped.id = id;
    compareAvailabilityById[id] = {
      status: "available",
      confirmed: true,
      diamond: mapped,
    };
    return;
  }
  if (status === "unavailable" || status === "not_found" || status === "invalid") {
    compareAvailabilityById[id] = {
      status: status,
      confirmed: true,
      diamond: null,
    };
    return;
  }
  if (status === "error") {
    const prior = compareAvailabilityById[id];
    if (
      prior &&
      prior.confirmed &&
      (prior.status === "available" || prior.status === "unavailable")
    ) {
      return;
    }
  }
}

let compareBatchPending = false;

function requestCompareBatch(ids) {
  if (!ids || !ids.length) {
    compareBatchPending = false;
    syncCompareControl();
    return;
  }
  if (!window.IvanovAPI || typeof window.IvanovAPI.getDiamondsBatch !== "function") {
    compareBatchPending = false;
    syncCompareControl();
    return;
  }
  const generation = ++compareBatchGeneration;
  compareBatchPending = true;
  if (compareBatchAbortController) {
    compareBatchAbortController.abort();
  }
  compareBatchAbortController =
    typeof AbortController === "function" ? new AbortController() : null;
  const signal = compareBatchAbortController ? compareBatchAbortController.signal : undefined;
  const requested = ids.slice();
  window.IvanovAPI
    .getDiamondsBatch(requested, signal ? { signal: signal } : undefined)
    .then((response) => {
      if (generation !== compareBatchGeneration) return;
      compareBatchPending = false;
      const results = response && Array.isArray(response.results) ? response.results : null;
      if (!results) {
        syncCompareControl();
        return;
      }
      results.forEach(applyCompareBatchRow);
      syncCompareControl();
    })
    .catch((err) => {
      if (isIntentionalAbortError(err)) return;
      if (generation !== compareBatchGeneration) return;
      compareBatchPending = false;
      syncCompareControl();
    });
}

function onSearchCompareSavedChange() {
  pruneCompareAvailability();
  const unknown = unclassifiedCompareIds();
  syncCompareControl();
  if (!unknown.length) return;
  requestCompareBatch(unknown);
}

function bindCompareControl() {
  const button = document.getElementById("diamondSearchCompare");
  if (!button || button.getAttribute("data-compare-bound") === "true") return;
  button.setAttribute("data-compare-bound", "true");
  button.addEventListener("click", (event) => {
    if (event && typeof event.preventDefault === "function") event.preventDefault();
    const compare = window.DiamondSearchCompare;
    if (compare && typeof compare.enter === "function") compare.enter();
  });
}

function onSearchComparePageShow(event) {
  if (!event || event.persisted !== true) return;
  const Saved = window.DiamondSaved;
  if (!Saved || typeof Saved.refreshFromStorage !== "function") return;
  try {
    Saved.refreshFromStorage();
  } catch (err) {
    // Saved resync must not affect Viewer return or Search filters.
  }
}

function initSearchCompareAvailability() {
  try {
    if (!searchCompareListenersBound && typeof window.addEventListener === "function") {
      searchCompareListenersBound = true;
      window.addEventListener("ivanov:saved-diamonds-change", onSearchCompareSavedChange);
      window.addEventListener("pageshow", onSearchComparePageShow);
    }
    bindCompareControl();
    const unknown = unclassifiedCompareIds();
    if (!unknown.length) {
      syncCompareControl();
      return;
    }
    requestCompareBatch(unknown);
  } catch (err) {
    // Compare availability must not break Search startup.
  }
}

function showCachedSearchResults() {
  compareResultCountOverride = null;
  compareHoldsSearchGrid = false;
  if (searchFetchInFlight) return;
  applyFilters();
  syncLoadMoreAfterResults();
}

window.DiamondSearchCompareBridge = {
  listAvailableDiamonds: listAvailableCompareDiamonds,
  availableCount: compareAvailableCount,
  unclassifiedCount: function () {
    return unclassifiedCompareIds().length;
  },
  isBatchPending: function () {
    return compareBatchPending;
  },
  holdSearchGrid: function (hold) {
    compareHoldsSearchGrid = hold === true;
  },
  isHoldingSearchGrid: function () {
    return compareHoldsSearchGrid;
  },
  setResultCountOverride: function (value) {
    compareResultCountOverride = value == null ? null : value;
  },
  hideLoadMore: function () {
    setLoadMoreUi("hidden");
  },
  restoreLoadMore: function () {
    syncLoadMoreAfterResults();
  },
  showCachedSearch: showCachedSearchResults,
  createCard: createDiamondCard,
  restoreFiltersWithoutReload: function () {
    restoreFiltersFromUrl({ skipReload: true });
  },
  refreshControl: syncCompareControl,
};

// ————————————————————————————————————————————
// 5) Kick it all off
// ————————————————————————————————————————————
document.addEventListener("DOMContentLoaded", () => {
  bindDiamondResultsRetry();
  bindDiamondLoadMore();

  // Origin + Shape + Color + Clarity + Cut + Polish + Symmetry + Fluorescence + Certificate Lab + Geometry + Girdle + Culet + Media + Availability + Carat + Price + Sort from URL before the first request.
  initOriginFromUrl();
  initShapeFromUrl();
  initColorFromUrl();
  initClarityFromUrl();
  initCutFromUrl();
  initPolishFromUrl();
  initSymmetryFromUrl();
  initFluorescenceFromUrl();
  initCertificateLabFromUrl();
  GEOMETRY_RANGE_CONFIGS.forEach((cfg) => geometryRangeControllers[cfg.key].initFromUrl());
  initGirdleFromUrl();
  initCuletFromUrl();
  initMediaHasImageFromUrl();
  initMediaHasV360FromUrl();
  initMediaHasCertPdfFromUrl();
  initAvailabilityIsFbnFromUrl();
  initAvailabilityReturnsFromUrl();
  initAvailabilityHasTracerFromUrl();
  initAvailabilityDeliveryTimeFromUrl();
  initCaratFromUrl();
  initPriceFromUrl();
  initSortFromUrl();
  // Tell the hero to re-read Origin/Shape after URL init (no title logic here).
  document.dispatchEvent(new CustomEvent("diamond:origin-ui-ready"));
  // First request must include Shape/Color/Clarity/Cut/Polish/Symmetry/Fluorescence/Certificate Lab/Geometry/Girdle/Culet/Media/Availability/Carat/Price/Sort when the URL restored them.
  // Search paints on its own. Compare availability runs beside it and
  // must not await or share reloadAbortController.
  reloadDiamonds(collectActiveServerFilters());
  initSearchCompareAvailability();
  initListeners();
  initUrlNavigation();
});
